import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, ArrowLeft, BookOpen, ChevronDown, ChevronUp, Phone, Lock, User, X, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// PGT Syllabus Data
const pgtSyllabusData = {
  mathematics: {
    title: "EMRS PGT Mathematics Syllabus (Post-Graduation Level)",
    sections: [
      { name: "Sets : Set theory axioms and operations", topics: ["Set theory axioms", "Set operations", "Algebra of sets"] },
      { name: "Sets : Cardinality of finite and infinite sets", topics: ["Finite sets", "Infinite sets", "Cardinality"] },
      { name: "Sets : Power set and Cartesian product", topics: ["Power set", "Cartesian product", "Applications"] },
      { name: "Sets : De Morgan's laws and algebra of sets", topics: ["De Morgan's laws", "Set algebra", "Proofs"] },
      { name: "Sets : Relations between sets and Venn diagram applications", topics: ["Set relations", "Venn diagrams", "Applications"] },
      { name: "Relations & Functions : Types of relations (reflexive, symmetric, transitive)", topics: ["Reflexive relations", "Symmetric relations", "Transitive relations"] },
      { name: "Relations & Functions : Equivalence relations and partitions", topics: ["Equivalence relations", "Partitions", "Equivalence classes"] },
      { name: "Relations & Functions : Injective, surjective and bijective functions", topics: ["Injective functions", "Surjective functions", "Bijective functions"] },
      { name: "Relations & Functions : Composition and inverse of functions", topics: ["Function composition", "Inverse functions", "Properties"] },
      { name: "Relations & Functions : Real valued functions and graphical interpretation", topics: ["Real functions", "Graphs", "Interpretation"] },
      { name: "Trigonometric Functions : Unit circle approach", topics: ["Unit circle", "Trigonometric ratios", "Applications"] },
      { name: "Trigonometric Functions : Trigonometric identities and transformations", topics: ["Identities", "Transformations", "Proofs"] },
      { name: "Trigonometric Functions : Inverse trigonometric functions", topics: ["Inverse functions", "Domain and range", "Properties"] },
      { name: "Trigonometric Functions : Applications in solving equations", topics: ["Trigonometric equations", "General solutions", "Applications"] },
      { name: "Trigonometric Functions : Graphical analysis of trigonometric functions", topics: ["Graphs", "Period", "Amplitude"] },
      { name: "Complex Numbers and Quadratic Equations : Algebra of complex numbers", topics: ["Complex algebra", "Operations", "Properties"] },
      { name: "Complex Numbers and Quadratic Equations : Argand plane representation", topics: ["Argand plane", "Geometric interpretation", "Modulus and argument"] },
      { name: "Complex Numbers and Quadratic Equations : Polar form and De Moivre's theorem", topics: ["Polar form", "De Moivre's theorem", "Applications"] },
      { name: "Complex Numbers and Quadratic Equations : Roots of unity", topics: ["nth roots of unity", "Properties", "Applications"] },
      { name: "Matrices : Matrix algebra and operations", topics: ["Matrix operations", "Types of matrices", "Properties"] },
      { name: "Matrices : Rank and linear independence", topics: ["Rank of matrix", "Linear independence", "Row reduction"] },
      { name: "Matrices : Eigenvalues and eigenvectors", topics: ["Eigenvalues", "Eigenvectors", "Characteristic equation"] },
      { name: "Matrices : Diagonalization", topics: ["Diagonalization", "Similar matrices", "Applications"] },
      { name: "Matrices : Applications to linear systems", topics: ["Linear systems", "Solutions", "Cramer's rule"] },
      { name: "Determinants : Expansion and properties", topics: ["Determinant expansion", "Properties", "Cofactors"] },
      { name: "Determinants : Adjoint and inverse", topics: ["Adjoint matrix", "Matrix inverse", "Applications"] },
      { name: "Determinants : Applications in solving linear equations", topics: ["Cramer's rule", "Linear systems", "Consistency"] },
      { name: "Determinants : Area and volume interpretation", topics: ["Area using determinants", "Volume", "Geometric applications"] },
      { name: "Continuity and Differentiability : Limit definition (epsilon-delta)", topics: ["Epsilon-delta definition", "Limits", "Continuity"] },
      { name: "Continuity and Differentiability : Mean Value Theorems", topics: ["Rolle's theorem", "Mean value theorem", "Applications"] },
      { name: "Continuity and Differentiability : Implicit differentiation", topics: ["Implicit functions", "Differentiation", "Applications"] },
      { name: "Continuity and Differentiability : Higher order derivatives", topics: ["Higher derivatives", "Leibniz rule", "Applications"] },
      { name: "Integrals : Indefinite and definite integration", topics: ["Indefinite integrals", "Definite integrals", "Techniques"] },
      { name: "Integrals : Substitution and integration by parts", topics: ["Substitution method", "Integration by parts", "Applications"] },
      { name: "Integrals : Applications to area and volume", topics: ["Area under curves", "Volume of revolution", "Applications"] },
      { name: "Integrals : Fundamental Theorem of Calculus", topics: ["FTC Part 1", "FTC Part 2", "Applications"] },
      { name: "Differential Equations : First order differential equations", topics: ["First order ODEs", "Methods", "Applications"] },
      { name: "Differential Equations : Linear differential equations", topics: ["Linear ODEs", "Integrating factor", "Solutions"] },
      { name: "Differential Equations : Homogeneous equations", topics: ["Homogeneous ODEs", "Substitution method", "Solutions"] },
      { name: "Differential Equations : Applications in growth and decay models", topics: ["Growth models", "Decay models", "Applications"] },
      { name: "Vectors : Vector algebra", topics: ["Vector operations", "Properties", "Applications"] },
      { name: "Vectors : Dot and cross product", topics: ["Dot product", "Cross product", "Applications"] },
      { name: "Vectors : Scalar triple product", topics: ["Scalar triple product", "Volume", "Coplanarity"] },
      { name: "Vectors : Geometric applications", topics: ["Lines", "Planes", "Geometric problems"] },
      { name: "Three-Dimensional Geometry : Direction cosines and ratios", topics: ["Direction cosines", "Direction ratios", "Angle between lines"] },
      { name: "Three-Dimensional Geometry : Equation of line and plane", topics: ["Line equations", "Plane equations", "Forms"] },
      { name: "Three-Dimensional Geometry : Shortest distance problems", topics: ["Distance between lines", "Distance from point", "Applications"] },
      { name: "Three-Dimensional Geometry : Angle between lines and planes", topics: ["Angle between lines", "Angle between planes", "Applications"] },
      { name: "Linear Programming : Formulation of optimization problems", topics: ["Problem formulation", "Constraints", "Objective function"] },
      { name: "Linear Programming : Graphical solution method", topics: ["Graphical method", "Corner points", "Optimal solution"] },
      { name: "Linear Programming : Feasible region and optimal solution", topics: ["Feasible region", "Bounded/unbounded", "Optimal solution"] },
      { name: "Probability : Axiomatic probability theory", topics: ["Axioms", "Probability space", "Events"] },
      { name: "Probability : Conditional probability and Bayes' theorem", topics: ["Conditional probability", "Bayes' theorem", "Applications"] },
      { name: "Probability : Random variables and distributions", topics: ["Random variables", "Distributions", "PDF and CDF"] },
      { name: "Probability : Mean and variance", topics: ["Expected value", "Variance", "Standard deviation"] }
    ]
  },
  physics: {
    title: "EMRS PGT Physics Syllabus (Post-Graduation Level)",
    sections: [
      { name: "Units and Measurements : Dimensional analysis and Buckingham π theorem", topics: ["Dimensional analysis", "Buckingham π theorem", "Applications"] },
      { name: "Units and Measurements : Error analysis and propagation of errors", topics: ["Error types", "Error propagation", "Significant figures"] },
      { name: "Units and Measurements : Significant figures and precision techniques", topics: ["Significant figures", "Precision", "Accuracy"] },
      { name: "Units and Measurements : Systems of units and SI standards", topics: ["SI units", "Fundamental units", "Derived units"] },
      { name: "Motion in a Straight Line : Kinematics using calculus", topics: ["Calculus in kinematics", "Derivatives", "Integrals"] },
      { name: "Motion in a Straight Line : Velocity and acceleration as derivatives", topics: ["Velocity", "Acceleration", "Instantaneous values"] },
      { name: "Motion in a Straight Line : Relative motion in one dimension", topics: ["Relative velocity", "Reference frames", "Applications"] },
      { name: "Motion in a Straight Line : Graphical and analytical solutions", topics: ["Position-time graphs", "Velocity-time graphs", "Analysis"] },
      { name: "Motion in a Plane : Vector algebra and vector calculus basics", topics: ["Vector algebra", "Vector calculus", "Applications"] },
      { name: "Motion in a Plane : Projectile motion (range, time, maximum height)", topics: ["Projectile motion", "Range formula", "Maximum height"] },
      { name: "Motion in a Plane : Circular motion and centripetal acceleration", topics: ["Circular motion", "Centripetal acceleration", "Applications"] },
      { name: "Motion in a Plane : Relative velocity in two dimensions", topics: ["2D relative motion", "Vector addition", "Applications"] },
      { name: "Laws of Motion : Newtonian mechanics in inertial frames", topics: ["Newton's laws", "Inertial frames", "Applications"] },
      { name: "Laws of Motion : Non-inertial frames and pseudo forces", topics: ["Non-inertial frames", "Pseudo forces", "Applications"] },
      { name: "Laws of Motion : Friction (static, kinetic, rolling)", topics: ["Static friction", "Kinetic friction", "Rolling friction"] },
      { name: "Laws of Motion : Applications in circular motion dynamics", topics: ["Circular motion dynamics", "Banking", "Applications"] },
      { name: "Work, Energy and Power : Work-energy theorem", topics: ["Work-energy theorem", "Kinetic energy", "Applications"] },
      { name: "Work, Energy and Power : Conservative and non-conservative forces", topics: ["Conservative forces", "Non-conservative forces", "Work done"] },
      { name: "Work, Energy and Power : Potential energy functions", topics: ["Potential energy", "Conservative forces", "Applications"] },
      { name: "Work, Energy and Power : Collisions (elastic and inelastic)", topics: ["Elastic collisions", "Inelastic collisions", "Momentum"] },
      { name: "Motion of System of Particles and Rigid Body : Center of mass", topics: ["Center of mass", "Motion of COM", "Applications"] },
      { name: "Motion of System of Particles and Rigid Body : Angular momentum conservation", topics: ["Angular momentum", "Conservation", "Applications"] },
      { name: "Motion of System of Particles and Rigid Body : Moment of inertia", topics: ["Moment of inertia", "Parallel axis theorem", "Applications"] },
      { name: "Motion of System of Particles and Rigid Body : Rotational kinetic energy", topics: ["Rotational KE", "Rolling motion", "Applications"] },
      { name: "Gravitation : Gravitational field and potential", topics: ["Gravitational field", "Gravitational potential", "Applications"] },
      { name: "Gravitation : Kepler's laws and orbital mechanics", topics: ["Kepler's laws", "Orbital mechanics", "Satellite motion"] },
      { name: "Gravitation : Escape velocity and satellite motion", topics: ["Escape velocity", "Satellite motion", "Orbital velocity"] },
      { name: "Gravitation : Variation of g with altitude and depth", topics: ["Variation of g", "Altitude effect", "Depth effect"] },
      { name: "Thermodynamics : Laws of thermodynamics", topics: ["First law", "Second law", "Third law"] },
      { name: "Thermodynamics : Entropy and spontaneity", topics: ["Entropy", "Spontaneity", "Free energy"] },
      { name: "Thermodynamics : Carnot cycle and efficiency", topics: ["Carnot cycle", "Efficiency", "Heat engines"] },
      { name: "Thermodynamics : Thermodynamic potentials", topics: ["Internal energy", "Enthalpy", "Free energy"] },
      { name: "Oscillations and Waves : Simple harmonic motion (differential equation form)", topics: ["SHM equation", "Solutions", "Applications"] },
      { name: "Oscillations and Waves : Damped and forced oscillations", topics: ["Damped oscillations", "Forced oscillations", "Resonance"] },
      { name: "Oscillations and Waves : Wave equation", topics: ["Wave equation", "Solutions", "Properties"] },
      { name: "Oscillations and Waves : Superposition and standing waves", topics: ["Superposition", "Standing waves", "Nodes and antinodes"] },
      { name: "Electric Charges and Fields : Coulomb's law and electric field intensity", topics: ["Coulomb's law", "Electric field", "Applications"] },
      { name: "Electric Charges and Fields : Gauss's law applications", topics: ["Gauss's law", "Electric flux", "Applications"] },
      { name: "Electrostatic Potential and Capacitance : Electric potential and field relation", topics: ["Electric potential", "Potential gradient", "Applications"] },
      { name: "Electrostatic Potential and Capacitance : Capacitance and dielectric materials", topics: ["Capacitance", "Dielectrics", "Energy stored"] },
      { name: "Current Electricity : Drift velocity and mobility", topics: ["Drift velocity", "Mobility", "Conductivity"] },
      { name: "Current Electricity : Kirchhoff's laws", topics: ["KCL", "KVL", "Circuit analysis"] },
      { name: "Current Electricity : Wheatstone bridge", topics: ["Wheatstone bridge", "Balanced condition", "Applications"] },
      { name: "Current Electricity : Electrical power and internal resistance", topics: ["Power", "Internal resistance", "Maximum power transfer"] },
      { name: "Magnetic Effects of Current : Biot–Savart law", topics: ["Biot-Savart law", "Magnetic field", "Applications"] },
      { name: "Magnetic Effects of Current : Ampere's circuital law", topics: ["Ampere's law", "Applications", "Solenoid and toroid"] },
      { name: "Electromagnetic Induction and Alternating Currents : Faraday's laws", topics: ["Faraday's law", "Induced EMF", "Applications"] },
      { name: "Electromagnetic Induction and Alternating Currents : LCR circuits and resonance", topics: ["LCR circuits", "Resonance", "Q-factor"] },
      { name: "Electromagnetic Waves : Maxwell's equations (conceptual)", topics: ["Maxwell's equations", "EM waves", "Properties"] },
      { name: "Electromagnetic Waves : Electromagnetic spectrum", topics: ["EM spectrum", "Types", "Applications"] },
      { name: "Electromagnetic Waves : Energy and momentum of EM waves", topics: ["Energy density", "Momentum", "Poynting vector"] },
      { name: "Ray Optics and Optical Instruments : Lens and mirror formula derivations", topics: ["Lens formula", "Mirror formula", "Magnification"] },
      { name: "Wave Optics : Huygens' principle", topics: ["Huygens' principle", "Wave propagation", "Applications"] },
      { name: "Wave Optics : Interference and diffraction", topics: ["Interference", "Diffraction", "Young's experiment"] },
      { name: "Wave Optics : Polarization", topics: ["Polarization", "Malus law", "Polarizers"] },
      { name: "Dual Nature of Radiation and Matter : Photoelectric effect", topics: ["Photoelectric effect", "Einstein's equation", "Applications"] },
      { name: "Atoms & Nuclei : Bohr's model and hydrogen spectrum", topics: ["Bohr model", "Hydrogen spectrum", "Energy levels"] },
      { name: "Atoms & Nuclei : Nuclear binding energy and fission/fusion", topics: ["Binding energy", "Fission", "Fusion"] },
      { name: "Semiconductor Electronics : p-n junction and rectifiers", topics: ["p-n junction", "Diode", "Rectifiers"] }
    ]
  },
  chemistry: {
    title: "EMRS PGT Chemistry Syllabus (Post-Graduation Level)",
    sections: [
      { name: "Basic Concepts of Chemistry : Mole concept and stoichiometric calculations", topics: ["Mole concept", "Stoichiometry", "Calculations"] },
      { name: "Basic Concepts of Chemistry : Laws of chemical combination", topics: ["Law of conservation", "Law of definite proportions", "Applications"] },
      { name: "Basic Concepts of Chemistry : Empirical and molecular formula determination", topics: ["Empirical formula", "Molecular formula", "Calculations"] },
      { name: "Basic Concepts of Chemistry : Limiting reagent and percentage yield", topics: ["Limiting reagent", "Percentage yield", "Applications"] },
      { name: "Structure of Atom : Quantum mechanical model of atom", topics: ["Quantum model", "Wave function", "Orbitals"] },
      { name: "Structure of Atom : de Broglie wavelength and wave-particle duality", topics: ["de Broglie wavelength", "Wave-particle duality", "Applications"] },
      { name: "Structure of Atom : Heisenberg uncertainty principle", topics: ["Uncertainty principle", "Implications", "Applications"] },
      { name: "Structure of Atom : Schrödinger wave equation (conceptual)", topics: ["Schrödinger equation", "Wave function", "Probability"] },
      { name: "Structure of Atom : Quantum numbers and electronic configuration", topics: ["Quantum numbers", "Electronic configuration", "Aufbau principle"] },
      { name: "Classification of Elements and Periodicity in Properties : Modern periodic law", topics: ["Periodic law", "Periodic table", "Groups and periods"] },
      { name: "Classification of Elements and Periodicity in Properties : Periodic trends (IE, EA, EN)", topics: ["Ionization energy", "Electron affinity", "Electronegativity"] },
      { name: "Classification of Elements and Periodicity in Properties : Atomic and ionic radii trends", topics: ["Atomic radii", "Ionic radii", "Trends"] },
      { name: "Classification of Elements and Periodicity in Properties : Periodicity in chemical reactivity", topics: ["Chemical reactivity", "Periodic trends", "Applications"] },
      { name: "Chemical Bonding and Molecular Structure : Valence bond theory", topics: ["VBT", "Sigma and pi bonds", "Applications"] },
      { name: "Chemical Bonding and Molecular Structure : VSEPR theory", topics: ["VSEPR theory", "Molecular geometry", "Predictions"] },
      { name: "Chemical Bonding and Molecular Structure : Hybridization (sp, sp2, sp3, dsp2, sp3d2)", topics: ["Hybridization types", "Geometry", "Examples"] },
      { name: "Chemical Bonding and Molecular Structure : Molecular orbital theory", topics: ["MOT", "Bonding and antibonding", "Bond order"] },
      { name: "Chemical Bonding and Molecular Structure : Hydrogen bonding", topics: ["Hydrogen bonding", "Types", "Properties"] },
      { name: "Chemical Thermodynamics : First law of thermodynamics", topics: ["First law", "Internal energy", "Work and heat"] },
      { name: "Chemical Thermodynamics : Enthalpy and Hess's law", topics: ["Enthalpy", "Hess's law", "Applications"] },
      { name: "Chemical Thermodynamics : Entropy and spontaneity", topics: ["Entropy", "Spontaneity", "Second law"] },
      { name: "Chemical Thermodynamics : Gibbs free energy and equilibrium", topics: ["Gibbs energy", "Equilibrium", "Spontaneity"] },
      { name: "Chemical Thermodynamics : Thermodynamic functions and criteria for spontaneity", topics: ["Thermodynamic functions", "Spontaneity criteria", "Applications"] },
      { name: "Equilibrium : Chemical equilibrium and law of mass action", topics: ["Chemical equilibrium", "Law of mass action", "Equilibrium constant"] },
      { name: "Equilibrium : Le Chatelier's principle", topics: ["Le Chatelier's principle", "Factors affecting equilibrium", "Applications"] },
      { name: "Equilibrium : Ionic equilibrium and pH", topics: ["Ionic equilibrium", "pH", "pOH"] },
      { name: "Equilibrium : Buffer solutions and Henderson equation", topics: ["Buffer solutions", "Henderson equation", "Applications"] },
      { name: "Equilibrium : Solubility product and common ion effect", topics: ["Solubility product", "Common ion effect", "Applications"] },
      { name: "Redox Reactions : Oxidation number concept", topics: ["Oxidation number", "Rules", "Applications"] },
      { name: "Redox Reactions : Balancing redox equations", topics: ["Balancing methods", "Half-reaction method", "Applications"] },
      { name: "Redox Reactions : Electrochemical cells (basic concept)", topics: ["Electrochemical cells", "EMF", "Applications"] },
      { name: "Redox Reactions : Applications of redox reactions", topics: ["Industrial applications", "Corrosion", "Batteries"] },
      { name: "Solutions : Concentration terms (molarity, molality, normality)", topics: ["Molarity", "Molality", "Normality"] },
      { name: "Solutions : Raoult's law", topics: ["Raoult's law", "Ideal solutions", "Non-ideal solutions"] },
      { name: "Solutions : Colligative properties", topics: ["Colligative properties", "Osmotic pressure", "Applications"] },
      { name: "Solutions : Van't Hoff factor", topics: ["Van't Hoff factor", "Abnormal molecular mass", "Applications"] },
      { name: "Electrochemistry : Nernst equation", topics: ["Nernst equation", "Cell potential", "Applications"] },
      { name: "Electrochemistry : EMF of cell", topics: ["EMF", "Standard electrode potential", "Electrochemical series"] },
      { name: "Electrochemistry : Conductance and Kohlrausch's law", topics: ["Conductance", "Kohlrausch's law", "Applications"] },
      { name: "Electrochemistry : Corrosion and electrochemical series", topics: ["Corrosion", "Prevention", "Electrochemical series"] },
      { name: "Chemical Kinetics : Rate law and order of reaction", topics: ["Rate law", "Order of reaction", "Rate constant"] },
      { name: "Chemical Kinetics : Integrated rate equations", topics: ["Zero order", "First order", "Second order"] },
      { name: "Chemical Kinetics : Arrhenius equation", topics: ["Arrhenius equation", "Activation energy", "Applications"] },
      { name: "Chemical Kinetics : Collision theory and activation energy", topics: ["Collision theory", "Activation energy", "Factors affecting rate"] },
      { name: "d and f Block Elements : Electronic configuration of transition elements", topics: ["Electronic configuration", "Transition elements", "Properties"] },
      { name: "d and f Block Elements : Oxidation states and complex formation", topics: ["Oxidation states", "Complex formation", "Applications"] },
      { name: "d and f Block Elements : Lanthanide contraction", topics: ["Lanthanide contraction", "Causes", "Effects"] },
      { name: "d and f Block Elements : Magnetic and catalytic properties", topics: ["Magnetic properties", "Catalytic properties", "Applications"] },
      { name: "Coordination Compounds : Werner's theory", topics: ["Werner's theory", "Coordination number", "Ligands"] },
      { name: "Coordination Compounds : Crystal field theory", topics: ["CFT", "Crystal field splitting", "Applications"] },
      { name: "Coordination Compounds : Nomenclature and isomerism", topics: ["IUPAC nomenclature", "Isomerism", "Types"] },
      { name: "Coordination Compounds : Applications in biological systems", topics: ["Biological applications", "Hemoglobin", "Chlorophyll"] },
      { name: "Organic Chemistry – Basic Principles : Inductive and resonance effects", topics: ["Inductive effect", "Resonance effect", "Applications"] },
      { name: "Organic Chemistry – Basic Principles : Reaction intermediates", topics: ["Carbocations", "Carbanions", "Free radicals"] },
      { name: "Organic Chemistry – Basic Principles : Types of organic reactions", topics: ["Substitution", "Addition", "Elimination"] },
      { name: "Organic Chemistry – Basic Principles : Mechanism of substitution and elimination", topics: ["SN1", "SN2", "E1", "E2"] },
      { name: "Hydrocarbons : Free radical halogenation", topics: ["Free radical mechanism", "Halogenation", "Applications"] },
      { name: "Hydrocarbons : Electrophilic addition reactions", topics: ["Electrophilic addition", "Mechanism", "Applications"] },
      { name: "Hydrocarbons : Aromaticity and electrophilic substitution", topics: ["Aromaticity", "Electrophilic substitution", "Benzene reactions"] },
      { name: "Hydrocarbons : Markovnikov and anti-Markovnikov addition", topics: ["Markovnikov's rule", "Anti-Markovnikov", "Applications"] },
      { name: "Alcohols, Phenols and Ethers : Acidic nature and hydrogen bonding", topics: ["Acidic nature", "Hydrogen bonding", "Properties"] },
      { name: "Alcohols, Phenols and Ethers : Dehydration and oxidation reactions", topics: ["Dehydration", "Oxidation", "Applications"] },
      { name: "Alcohols, Phenols and Ethers : Electrophilic substitution in phenols", topics: ["Electrophilic substitution", "Ortho-para directing", "Applications"] },
      { name: "Aldehydes, Ketones and Carboxylic Acids : Nucleophilic addition mechanism", topics: ["Nucleophilic addition", "Mechanism", "Applications"] },
      { name: "Aldehydes, Ketones and Carboxylic Acids : Aldol condensation", topics: ["Aldol condensation", "Cross aldol", "Applications"] },
      { name: "Aldehydes, Ketones and Carboxylic Acids : Acidic strength of carboxylic acids", topics: ["Acidic strength", "Factors", "Comparison"] },
      { name: "Amines : Basicity of amines", topics: ["Basicity", "Factors affecting", "Comparison"] },
      { name: "Amines : Diazotization and coupling reactions", topics: ["Diazotization", "Coupling reactions", "Dyes"] },
      { name: "Amines : Hofmann bromamide reaction", topics: ["Hofmann reaction", "Mechanism", "Applications"] },
      { name: "Biomolecules : Structure of carbohydrates", topics: ["Carbohydrate structure", "Monosaccharides", "Polysaccharides"] },
      { name: "Biomolecules : Protein structure (primary to quaternary)", topics: ["Primary structure", "Secondary structure", "Tertiary and quaternary"] },
      { name: "Biomolecules : Enzyme kinetics", topics: ["Enzyme kinetics", "Michaelis-Menten", "Inhibition"] },
      { name: "Biomolecules : Structure of DNA and RNA", topics: ["DNA structure", "RNA structure", "Base pairing"] }
    ]
  },
  biology: {
    title: "EMRS PGT Biology Syllabus (Post-Graduation Level)",
    sections: [
      { name: "The Living World : Taxonomy and systematics", topics: ["Taxonomy", "Systematics", "Classification"] },
      { name: "The Living World : Binomial nomenclature principles", topics: ["Binomial nomenclature", "Rules", "Examples"] },
      { name: "The Living World : Three-domain system (Woese classification)", topics: ["Three-domain system", "Woese classification", "Domains"] },
      { name: "The Living World : Concept of species and speciation", topics: ["Species concept", "Speciation", "Types"] },
      { name: "Biological Classification : Five kingdom classification", topics: ["Five kingdoms", "Characteristics", "Examples"] },
      { name: "Biological Classification : Prokaryotic vs eukaryotic organization", topics: ["Prokaryotes", "Eukaryotes", "Differences"] },
      { name: "Biological Classification : Lichens, viruses and viroids", topics: ["Lichens", "Viruses", "Viroids"] },
      { name: "Biological Classification : Evolutionary basis of classification", topics: ["Evolutionary classification", "Phylogeny", "Cladistics"] },
      { name: "Plant Kingdom : Algae – structure and life cycle", topics: ["Algae structure", "Life cycle", "Types"] },
      { name: "Plant Kingdom : Bryophytes and pteridophytes", topics: ["Bryophytes", "Pteridophytes", "Life cycles"] },
      { name: "Plant Kingdom : Gymnosperms – anatomy and reproduction", topics: ["Gymnosperm anatomy", "Reproduction", "Examples"] },
      { name: "Plant Kingdom : Evolution of vascular tissue", topics: ["Vascular tissue evolution", "Xylem", "Phloem"] },
      { name: "Animal Kingdom : Non-chordate phyla classification", topics: ["Non-chordates", "Phyla", "Characteristics"] },
      { name: "Animal Kingdom : Chordate characteristics", topics: ["Chordate features", "Classification", "Examples"] },
      { name: "Animal Kingdom : Evolutionary relationships among phyla", topics: ["Evolutionary relationships", "Phylogeny", "Cladograms"] },
      { name: "Animal Kingdom : Comparative anatomy", topics: ["Comparative anatomy", "Homology", "Analogy"] },
      { name: "Cell – The Unit of Life : Prokaryotic and eukaryotic cell structure", topics: ["Cell structure", "Prokaryotes", "Eukaryotes"] },
      { name: "Cell – The Unit of Life : Membrane transport mechanisms", topics: ["Membrane transport", "Active transport", "Passive transport"] },
      { name: "Cell – The Unit of Life : Cell organelles and ultrastructure", topics: ["Organelles", "Ultrastructure", "Functions"] },
      { name: "Cell – The Unit of Life : Cytoskeleton and cell motility", topics: ["Cytoskeleton", "Cell motility", "Components"] },
      { name: "Biomolecules : Structure and classification of proteins", topics: ["Protein structure", "Classification", "Functions"] },
      { name: "Biomolecules : Carbohydrates and glycobiology", topics: ["Carbohydrates", "Glycobiology", "Functions"] },
      { name: "Biomolecules : Lipids and membrane structure", topics: ["Lipids", "Membrane structure", "Functions"] },
      { name: "Biomolecules : Enzyme kinetics (Michaelis–Menten model)", topics: ["Enzyme kinetics", "Michaelis-Menten", "Km and Vmax"] },
      { name: "Cell Cycle and Cell Division : Phases of cell cycle", topics: ["Cell cycle phases", "G1, S, G2, M", "Regulation"] },
      { name: "Cell Cycle and Cell Division : Regulation of cell cycle", topics: ["Cell cycle regulation", "Checkpoints", "CDKs and cyclins"] },
      { name: "Cell Cycle and Cell Division : Mitosis and meiosis", topics: ["Mitosis", "Meiosis", "Differences"] },
      { name: "Cell Cycle and Cell Division : Genetic significance of meiosis", topics: ["Genetic significance", "Recombination", "Variation"] },
      { name: "Photosynthesis in Higher Plants : Light reactions", topics: ["Light reactions", "Photosystems", "Electron transport"] },
      { name: "Photosynthesis in Higher Plants : Calvin cycle", topics: ["Calvin cycle", "Carbon fixation", "Reactions"] },
      { name: "Photosynthesis in Higher Plants : C3, C4 and CAM pathways", topics: ["C3 pathway", "C4 pathway", "CAM pathway"] },
      { name: "Photosynthesis in Higher Plants : Photorespiration", topics: ["Photorespiration", "C2 cycle", "Significance"] },
      { name: "Respiration in Plants : Glycolysis", topics: ["Glycolysis", "Steps", "Energy yield"] },
      { name: "Respiration in Plants : Krebs cycle", topics: ["Krebs cycle", "Steps", "Products"] },
      { name: "Respiration in Plants : Electron transport chain", topics: ["ETC", "Complexes", "ATP synthesis"] },
      { name: "Respiration in Plants : ATP synthesis mechanism", topics: ["ATP synthesis", "Chemiosmosis", "ATP synthase"] },
      { name: "Breathing and Exchange of Gases : Respiratory mechanism", topics: ["Respiratory mechanism", "Breathing", "Gas exchange"] },
      { name: "Body Fluids and Circulation : Cardiac cycle and ECG", topics: ["Cardiac cycle", "ECG", "Heart sounds"] },
      { name: "Excretory Products and Elimination : Nephron and urine formation", topics: ["Nephron", "Urine formation", "Filtration"] },
      { name: "Locomotion and Movement : Muscle contraction mechanism", topics: ["Muscle contraction", "Sliding filament", "Mechanism"] },
      { name: "Neural Control and Coordination : Nerve impulse transmission", topics: ["Nerve impulse", "Action potential", "Synapse"] },
      { name: "Chemical Coordination and Integration : Hormonal regulation", topics: ["Hormones", "Endocrine glands", "Regulation"] },
      { name: "Sexual Reproduction in Flowering Plants : Double fertilization", topics: ["Double fertilization", "Embryo development", "Endosperm"] },
      { name: "Human Reproduction : Gametogenesis", topics: ["Gametogenesis", "Spermatogenesis", "Oogenesis"] },
      { name: "Reproductive Health : Assisted reproductive technologies", topics: ["ART", "IVF", "ICSI"] },
      { name: "Principles of Inheritance and Variation : Mendelian genetics", topics: ["Mendelian genetics", "Laws", "Inheritance patterns"] },
      { name: "Principles of Inheritance and Variation : Linkage and crossing over", topics: ["Linkage", "Crossing over", "Recombination"] },
      { name: "Molecular Basis of Inheritance : DNA replication and transcription", topics: ["DNA replication", "Transcription", "Mechanism"] },
      { name: "Molecular Basis of Inheritance : Gene regulation (lac operon)", topics: ["Gene regulation", "Lac operon", "Operons"] },
      { name: "Evolution : Darwinian evolution and Hardy–Weinberg principle", topics: ["Darwinian evolution", "Hardy-Weinberg", "Population genetics"] },
      { name: "Biotechnology – Principles and Processes : Recombinant DNA technology", topics: ["rDNA technology", "Cloning vectors", "Techniques"] },
      { name: "Biotechnology and its Application : GM crops and gene therapy", topics: ["GM crops", "Gene therapy", "Applications"] },
      { name: "Biotechnology and its Application : DNA fingerprinting", topics: ["DNA fingerprinting", "VNTR", "Applications"] },
      { name: "Organisms and Populations : Population growth models", topics: ["Population growth", "Exponential", "Logistic"] },
      { name: "Ecosystem : Energy flow and ecological pyramids", topics: ["Energy flow", "Ecological pyramids", "Food chains"] },
      { name: "Biodiversity and Conservation : Conservation strategies", topics: ["Conservation", "In-situ", "Ex-situ"] },
      { name: "Human Health and Diseases : Immunology and vaccines", topics: ["Immunology", "Vaccines", "Immunity"] }
    ]
  },
  commerce: {
    title: "EMRS PGT Commerce Syllabus (Post-Graduation Level)",
    sections: [
      { name: "Introduction to Accounting : Accounting principles and GAAP framework", topics: ["GAAP", "Accounting principles", "Framework"] },
      { name: "Introduction to Accounting : Accounting equation and dual aspect concept", topics: ["Accounting equation", "Dual aspect", "Applications"] },
      { name: "Introduction to Accounting : Qualitative characteristics of financial information", topics: ["Qualitative characteristics", "Relevance", "Reliability"] },
      { name: "Introduction to Accounting : Accounting standards (Ind AS overview)", topics: ["Ind AS", "IFRS", "Convergence"] },
      { name: "Theory Base of Accounting : Fundamental accounting assumptions", topics: ["Going concern", "Consistency", "Accrual"] },
      { name: "Theory Base of Accounting : Accrual vs cash basis accounting", topics: ["Accrual basis", "Cash basis", "Differences"] },
      { name: "Theory Base of Accounting : Revenue recognition and matching concept", topics: ["Revenue recognition", "Matching concept", "Applications"] },
      { name: "Theory Base of Accounting : Conservatism and materiality principles", topics: ["Conservatism", "Materiality", "Applications"] },
      { name: "Recording of Business Transactions : Journal, ledger and trial balance", topics: ["Journal", "Ledger", "Trial balance"] },
      { name: "Recording of Business Transactions : Subsidiary books", topics: ["Subsidiary books", "Special journals", "Types"] },
      { name: "Recording of Business Transactions : Bank reconciliation statement", topics: ["BRS", "Causes of difference", "Preparation"] },
      { name: "Recording of Business Transactions : Rectification of errors", topics: ["Error types", "Rectification", "Suspense account"] },
      { name: "Depreciation, Provisions and Reserves : Methods of depreciation (SLM & WDV)", topics: ["SLM", "WDV", "Comparison"] },
      { name: "Depreciation, Provisions and Reserves : Accounting treatment of disposal", topics: ["Asset disposal", "Accounting treatment", "Profit/Loss"] },
      { name: "Depreciation, Provisions and Reserves : Revenue and capital reserves", topics: ["Revenue reserves", "Capital reserves", "Differences"] },
      { name: "Depreciation, Provisions and Reserves : Provision for doubtful debts", topics: ["Provision for doubtful debts", "Bad debts", "Treatment"] },
      { name: "Financial Statements : Trading and Profit & Loss Account", topics: ["Trading account", "P&L account", "Preparation"] },
      { name: "Financial Statements : Balance sheet preparation", topics: ["Balance sheet", "Format", "Components"] },
      { name: "Financial Statements : Adjustments in final accounts", topics: ["Adjustments", "Types", "Treatment"] },
      { name: "Financial Statements : Cash flow statement", topics: ["Cash flow statement", "Operating", "Investing", "Financing"] },
      { name: "Accounting for Partnership Firms : Profit sharing and goodwill valuation", topics: ["Profit sharing", "Goodwill", "Valuation methods"] },
      { name: "Accounting for Partnership Firms : Admission of partner", topics: ["Admission", "Adjustments", "Capital accounts"] },
      { name: "Accounting for Partnership Firms : Retirement and death of partner", topics: ["Retirement", "Death", "Settlement"] },
      { name: "Accounting for Partnership Firms : Dissolution of firm", topics: ["Dissolution", "Realization account", "Settlement"] },
      { name: "Accounting for Share Capital : Issue and allotment of shares", topics: ["Share issue", "Allotment", "Calls"] },
      { name: "Accounting for Share Capital : Forfeiture and reissue", topics: ["Forfeiture", "Reissue", "Accounting"] },
      { name: "Accounting for Share Capital : ESOP and sweat equity", topics: ["ESOP", "Sweat equity", "Accounting"] },
      { name: "Accounting for Share Capital : Disclosure as per Companies Act", topics: ["Disclosures", "Companies Act", "Requirements"] },
      { name: "Accounting for Debentures : Issue at par, premium and discount", topics: ["Debenture issue", "Premium", "Discount"] },
      { name: "Accounting for Debentures : Redemption of debentures", topics: ["Redemption", "Methods", "Accounting"] },
      { name: "Accounting for Debentures : Writing off discount on issue", topics: ["Discount on issue", "Writing off", "Treatment"] },
      { name: "Financial Statement Analysis : Ratio analysis (liquidity, solvency, profitability)", topics: ["Ratio analysis", "Types", "Interpretation"] },
      { name: "Financial Statement Analysis : Activity ratios and turnover ratios", topics: ["Activity ratios", "Turnover ratios", "Analysis"] },
      { name: "Financial Statement Analysis : Comparative and common size statements", topics: ["Comparative statements", "Common size", "Analysis"] },
      { name: "Financial Statement Analysis : Interpretation and limitations", topics: ["Interpretation", "Limitations", "Uses"] },
      { name: "Nature and Significance of Management : Management as art, science and profession", topics: ["Management nature", "Art", "Science", "Profession"] },
      { name: "Nature and Significance of Management : Functions of management", topics: ["Management functions", "POSDCORB", "Applications"] },
      { name: "Nature and Significance of Management : Coordination and its importance", topics: ["Coordination", "Importance", "Techniques"] },
      { name: "Principles of Management : Fayol's principles", topics: ["Fayol's principles", "14 principles", "Applications"] },
      { name: "Principles of Management : Taylor's scientific management", topics: ["Scientific management", "Principles", "Techniques"] },
      { name: "Principles of Management : Application in modern business", topics: ["Modern applications", "Relevance", "Case studies"] },
      { name: "Business Environment : Economic, political and legal environment", topics: ["Business environment", "Components", "Analysis"] },
      { name: "Business Environment : Globalization and liberalization", topics: ["Globalization", "Liberalization", "Impact"] },
      { name: "Business Environment : Demonetization and GST", topics: ["Demonetization", "GST", "Impact on business"] },
      { name: "Financial Management : Capital structure decisions", topics: ["Capital structure", "Optimal structure", "Theories"] },
      { name: "Financial Management : Investment and financing decisions", topics: ["Investment decisions", "Financing decisions", "Analysis"] },
      { name: "Financial Management : Working capital management", topics: ["Working capital", "Components", "Management"] },
      { name: "Financial Management : Dividend decision", topics: ["Dividend decision", "Theories", "Factors"] },
      { name: "Marketing : Marketing mix (4Ps)", topics: ["Marketing mix", "4Ps", "Applications"] },
      { name: "Marketing : Branding, packaging and labeling", topics: ["Branding", "Packaging", "Labeling"] },
      { name: "Marketing : Advertising and sales promotion", topics: ["Advertising", "Sales promotion", "Techniques"] },
      { name: "Marketing : Consumer protection Act 2019", topics: ["Consumer protection", "Act 2019", "Rights and remedies"] },
      { name: "Small Business and Enterprises : MSME development", topics: ["MSME", "Development", "Government schemes"] },
      { name: "Small Business and Enterprises : Start-up India scheme", topics: ["Start-up India", "Benefits", "Eligibility"] },
      { name: "Small Business and Enterprises : Role of NSIC and DIC", topics: ["NSIC", "DIC", "Functions"] },
      { name: "Small Business and Enterprises : Intellectual Property Rights", topics: ["IPR", "Patents", "Trademarks", "Copyrights"] }
    ]
  },
  economics: {
    title: "EMRS PGT Economics Syllabus (Post-Graduation Level)",
    sections: [
      { name: "Introduction : Nature and scope of Economics (Micro vs Macro)", topics: ["Economics nature", "Micro economics", "Macro economics"] },
      { name: "Introduction : Positive and normative economics", topics: ["Positive economics", "Normative economics", "Differences"] },
      { name: "Introduction : Central problems of an economy", topics: ["Central problems", "What, How, For whom", "Economic systems"] },
      { name: "Introduction : Production Possibility Frontier (PPF) analysis", topics: ["PPF", "Opportunity cost", "Efficiency"] },
      { name: "Collection, Organisation and Presentation of Data : Primary and secondary data", topics: ["Primary data", "Secondary data", "Collection methods"] },
      { name: "Collection, Organisation and Presentation of Data : Sampling techniques", topics: ["Sampling", "Types", "Applications"] },
      { name: "Collection, Organisation and Presentation of Data : Frequency distribution", topics: ["Frequency distribution", "Class intervals", "Tables"] },
      { name: "Collection, Organisation and Presentation of Data : Diagrammatic and graphical representation", topics: ["Diagrams", "Graphs", "Types"] },
      { name: "Statistical Tools and Interpretation : Measures of central tendency", topics: ["Mean", "Median", "Mode"] },
      { name: "Statistical Tools and Interpretation : Correlation (Karl Pearson & Spearman)", topics: ["Correlation", "Karl Pearson", "Spearman"] },
      { name: "Statistical Tools and Interpretation : Index numbers", topics: ["Index numbers", "Types", "Construction"] },
      { name: "Statistical Tools and Interpretation : Inflation measurement", topics: ["Inflation measurement", "CPI", "WPI"] },
      { name: "Consumer's Equilibrium and Demand : Utility analysis", topics: ["Utility analysis", "Cardinal utility", "Consumer equilibrium"] },
      { name: "Consumer's Equilibrium and Demand : Indifference curve analysis", topics: ["Indifference curves", "Budget line", "Consumer equilibrium"] },
      { name: "Consumer's Equilibrium and Demand : Price elasticity of demand", topics: ["Price elasticity", "Types", "Measurement"] },
      { name: "Consumer's Equilibrium and Demand : Law of demand and determinants", topics: ["Law of demand", "Determinants", "Exceptions"] },
      { name: "Producer Behaviour and Supply : Production function (short-run & long-run)", topics: ["Production function", "Short-run", "Long-run"] },
      { name: "Producer Behaviour and Supply : Cost curves and revenue concepts", topics: ["Cost curves", "Revenue concepts", "Relationships"] },
      { name: "Producer Behaviour and Supply : Producer's equilibrium", topics: ["Producer equilibrium", "MR=MC approach", "Profit maximization"] },
      { name: "Producer Behaviour and Supply : Elasticity of supply", topics: ["Elasticity of supply", "Determinants", "Measurement"] },
      { name: "Forms of Market and Price Determination : Perfect competition", topics: ["Perfect competition", "Features", "Price determination"] },
      { name: "Forms of Market and Price Determination : Monopoly and monopolistic competition", topics: ["Monopoly", "Monopolistic competition", "Differences"] },
      { name: "Forms of Market and Price Determination : Market equilibrium analysis", topics: ["Market equilibrium", "Shifts", "Analysis"] },
      { name: "Forms of Market and Price Determination : Price ceiling and price floor", topics: ["Price ceiling", "Price floor", "Effects"] },
      { name: "National Income and Related Aggregates : Methods of measuring national income", topics: ["Income method", "Expenditure method", "Output method"] },
      { name: "National Income and Related Aggregates : GDP, GNP, NDP and NNP", topics: ["GDP", "GNP", "NDP", "NNP"] },
      { name: "National Income and Related Aggregates : Nominal vs Real GDP", topics: ["Nominal GDP", "Real GDP", "GDP deflator"] },
      { name: "National Income and Related Aggregates : GDP and welfare", topics: ["GDP and welfare", "Limitations", "Green GDP"] },
      { name: "Money and Banking : Functions of money", topics: ["Money functions", "Primary", "Secondary"] },
      { name: "Money and Banking : Money supply and credit creation", topics: ["Money supply", "Credit creation", "Money multiplier"] },
      { name: "Money and Banking : Central bank functions (RBI)", topics: ["RBI functions", "Monetary authority", "Banker to government"] },
      { name: "Money and Banking : Monetary policy instruments", topics: ["Monetary policy", "Quantitative tools", "Qualitative tools"] },
      { name: "Determination of Income and Employment : Aggregate demand and supply", topics: ["Aggregate demand", "Aggregate supply", "Equilibrium"] },
      { name: "Determination of Income and Employment : Keynesian multiplier", topics: ["Keynesian multiplier", "Investment multiplier", "Applications"] },
      { name: "Determination of Income and Employment : Excess and deficient demand", topics: ["Excess demand", "Deficient demand", "Measures"] },
      { name: "Determination of Income and Employment : Fiscal and monetary measures", topics: ["Fiscal measures", "Monetary measures", "Applications"] },
      { name: "Government Budget and the Economy : Revenue and capital receipts", topics: ["Revenue receipts", "Capital receipts", "Classification"] },
      { name: "Government Budget and the Economy : Revenue and capital expenditure", topics: ["Revenue expenditure", "Capital expenditure", "Classification"] },
      { name: "Government Budget and the Economy : Fiscal deficit and budget deficit", topics: ["Fiscal deficit", "Budget deficit", "Primary deficit"] },
      { name: "Government Budget and the Economy : Public debt", topics: ["Public debt", "Internal debt", "External debt"] },
      { name: "Balance of Payments : Current and capital account", topics: ["Current account", "Capital account", "Components"] },
      { name: "Balance of Payments : Exchange rate determination", topics: ["Exchange rate", "Determination", "Systems"] },
      { name: "Balance of Payments : Fixed vs flexible exchange rate", topics: ["Fixed exchange rate", "Flexible exchange rate", "Comparison"] },
      { name: "Balance of Payments : Managed floating system", topics: ["Managed floating", "Dirty floating", "Applications"] },
      { name: "Development Experience (1947–90) : Planning in India", topics: ["Five year plans", "NITI Aayog", "Planning"] },
      { name: "Development Experience (1947–90) : Agricultural reforms", topics: ["Agricultural reforms", "Green revolution", "Land reforms"] },
      { name: "Development Experience (1947–90) : Industrial policy", topics: ["Industrial policy", "PSUs", "License raj"] },
      { name: "Development Experience (1947–90) : Import substitution strategy", topics: ["Import substitution", "ISI", "Effects"] },
      { name: "Economic Reforms Since 1991 : Liberalization, Privatization, Globalization", topics: ["LPG reforms", "Impact", "Assessment"] },
      { name: "Economic Reforms Since 1991 : GST and demonetization", topics: ["GST", "Demonetization", "Impact"] },
      { name: "Economic Reforms Since 1991 : Structural reforms", topics: ["Structural reforms", "Financial sector", "Industrial reforms"] },
      { name: "Economic Reforms Since 1991 : Impact assessment", topics: ["Impact assessment", "Growth", "Employment"] },
      { name: "Current Challenges Facing Indian Economy : Human capital formation", topics: ["Human capital", "Education", "Health"] },
      { name: "Current Challenges Facing Indian Economy : Rural development", topics: ["Rural development", "Programs", "Challenges"] },
      { name: "Current Challenges Facing Indian Economy : Employment and unemployment", topics: ["Employment", "Unemployment", "Measures"] },
      { name: "Current Challenges Facing Indian Economy : Sustainable development", topics: ["Sustainable development", "SDGs", "Challenges"] }
    ]
  },
  geography: {
    title: "EMRS PGT Geography Syllabus (Post-Graduation Level)",
    sections: [
      { name: "Human Geography – Nature and Scope : Evolution of human geography (determinism to possibilism)", topics: ["Evolution", "Determinism", "Possibilism"] },
      { name: "Human Geography – Nature and Scope : Areal differentiation and spatial organization", topics: ["Areal differentiation", "Spatial organization", "Concepts"] },
      { name: "Human Geography – Nature and Scope : Branches of human geography", topics: ["Branches", "Economic geography", "Political geography"] },
      { name: "Human Geography – Nature and Scope : Approaches – quantitative and behavioral", topics: ["Quantitative approach", "Behavioral approach", "Applications"] },
      { name: "World Population : Demographic transition model", topics: ["DTM", "Stages", "Applications"] },
      { name: "World Population : Population composition and structure", topics: ["Population composition", "Age structure", "Sex ratio"] },
      { name: "World Population : Migration theories (Ravenstein, Lee)", topics: ["Migration theories", "Ravenstein", "Lee's model"] },
      { name: "World Population : Population policies", topics: ["Population policies", "Pro-natalist", "Anti-natalist"] },
      { name: "Human Activities : Primary, secondary and tertiary activities", topics: ["Primary activities", "Secondary activities", "Tertiary activities"] },
      { name: "Human Activities : Location theories (Weber's industrial location theory)", topics: ["Location theories", "Weber's theory", "Applications"] },
      { name: "Human Activities : Agricultural systems of the world", topics: ["Agricultural systems", "Types", "Distribution"] },
      { name: "Human Activities : Global trade patterns", topics: ["Trade patterns", "Major flows", "Trade blocs"] },
      { name: "Transport, Communication and Trade : Modes of transport", topics: ["Transport modes", "Land", "Water", "Air"] },
      { name: "Transport, Communication and Trade : Communication networks", topics: ["Communication networks", "Telecommunications", "Internet"] },
      { name: "Transport, Communication and Trade : International trade blocs", topics: ["Trade blocs", "EU", "ASEAN", "NAFTA"] },
      { name: "Transport, Communication and Trade : Globalization and trade organizations", topics: ["Globalization", "WTO", "IMF", "World Bank"] },
      { name: "Population – Distribution, Density and Growth : Census analysis", topics: ["Census", "Analysis", "Interpretation"] },
      { name: "Population – Distribution, Density and Growth : Population explosion", topics: ["Population explosion", "Causes", "Effects"] },
      { name: "Population – Distribution, Density and Growth : Urbanization trends", topics: ["Urbanization", "Trends", "Challenges"] },
      { name: "Human Settlements : Rural settlement patterns", topics: ["Rural settlements", "Patterns", "Types"] },
      { name: "Human Settlements : Urban morphology", topics: ["Urban morphology", "Structure", "Models"] },
      { name: "Human Settlements : Functional classification of towns", topics: ["Functional classification", "Types", "Examples"] },
      { name: "Human Settlements : Smart city concept", topics: ["Smart cities", "Features", "Examples"] },
      { name: "Resources and Development : Resource classification", topics: ["Resource classification", "Types", "Distribution"] },
      { name: "Resources and Development : Water resource management", topics: ["Water resources", "Management", "Conservation"] },
      { name: "Resources and Development : Mineral resources of India", topics: ["Mineral resources", "Distribution", "Conservation"] },
      { name: "Resources and Development : Sustainable resource use", topics: ["Sustainable use", "Conservation", "Management"] },
      { name: "Agriculture : Cropping patterns in India", topics: ["Cropping patterns", "Major crops", "Distribution"] },
      { name: "Agriculture : Green revolution", topics: ["Green revolution", "Impact", "Assessment"] },
      { name: "Agriculture : Agricultural productivity and reforms", topics: ["Productivity", "Reforms", "Challenges"] },
      { name: "Agriculture : Food security issues", topics: ["Food security", "PDS", "Challenges"] },
      { name: "Industries : Industrial regions of India", topics: ["Industrial regions", "Major centers", "Development"] },
      { name: "Industries : Industrial policy of India", topics: ["Industrial policy", "Evolution", "Current status"] },
      { name: "Industries : MSMEs and industrial growth", topics: ["MSMEs", "Role", "Growth"] },
      { name: "Industries : Industrial pollution", topics: ["Industrial pollution", "Types", "Control measures"] },
      { name: "Transport and Communication : Transport networks in India", topics: ["Transport networks", "Roads", "Railways", "Airways"] },
      { name: "Transport and Communication : Role in economic development", topics: ["Transport role", "Economic development", "Integration"] },
      { name: "Transport and Communication : Digital connectivity", topics: ["Digital connectivity", "Digital India", "E-governance"] },
      { name: "International Trade : India's export and import trends", topics: ["Export trends", "Import trends", "Major partners"] },
      { name: "International Trade : Balance of trade", topics: ["Balance of trade", "Trade deficit", "Analysis"] },
      { name: "International Trade : WTO and trade policies", topics: ["WTO", "Trade policies", "India's stand"] },
      { name: "International Trade : Trade agreements", topics: ["Trade agreements", "FTAs", "Regional agreements"] }
    ]
  },
  computer_science: {
    title: "EMRS PGT Computer Science Syllabus (Post-Graduation Level)",
    sections: [
      { name: "Computer System Overview : Functional units of computer", topics: ["Functional units", "Input", "Output", "Processing"] },
      { name: "Computer System Overview : Von Neumann architecture", topics: ["Von Neumann", "Architecture", "Components"] },
      { name: "Computer System Overview : Input-output organization", topics: ["I/O organization", "Interfaces", "Buses"] },
      { name: "Computer System Overview : Memory hierarchy", topics: ["Memory hierarchy", "Cache", "RAM", "Storage"] },
      { name: "Data Representation : Number systems (binary, octal, hexadecimal)", topics: ["Number systems", "Conversions", "Applications"] },
      { name: "Data Representation : Signed magnitude and 2's complement", topics: ["Signed magnitude", "2's complement", "Arithmetic"] },
      { name: "Data Representation : Floating point representation (IEEE standard)", topics: ["Floating point", "IEEE 754", "Precision"] },
      { name: "Data Representation : Character encoding (ASCII, Unicode)", topics: ["ASCII", "Unicode", "UTF-8"] },
      { name: "Boolean Algebra and Logic Gates : Boolean laws and theorems", topics: ["Boolean laws", "Theorems", "Simplification"] },
      { name: "Boolean Algebra and Logic Gates : De Morgan's theorem", topics: ["De Morgan's theorem", "Applications", "Proofs"] },
      { name: "Boolean Algebra and Logic Gates : Logic gate implementation", topics: ["Logic gates", "Circuits", "Implementation"] },
      { name: "Boolean Algebra and Logic Gates : Karnaugh map simplification", topics: ["K-map", "Simplification", "Applications"] },
      { name: "Programming in Python / C++ : Data types and variables", topics: ["Data types", "Variables", "Declarations"] },
      { name: "Programming in Python / C++ : Control structures", topics: ["Control structures", "Conditionals", "Loops"] },
      { name: "Programming in Python / C++ : Functions and recursion", topics: ["Functions", "Recursion", "Applications"] },
      { name: "Programming in Python / C++ : File handling", topics: ["File handling", "Read/Write", "Binary files"] },
      { name: "Programming in Python / C++ : Object-oriented programming", topics: ["OOP concepts", "Classes", "Inheritance"] },
      { name: "Data Structures : Arrays and linked lists", topics: ["Arrays", "Linked lists", "Operations"] },
      { name: "Data Structures : Stack and queue", topics: ["Stack", "Queue", "Applications"] },
      { name: "Data Structures : Trees and binary search trees", topics: ["Trees", "BST", "Traversals"] },
      { name: "Data Structures : Graphs and traversal algorithms", topics: ["Graphs", "BFS", "DFS"] },
      { name: "Data Structures : Searching and sorting algorithms", topics: ["Searching", "Sorting", "Complexity"] },
      { name: "Database Concepts : DBMS architecture", topics: ["DBMS architecture", "Components", "Functions"] },
      { name: "Database Concepts : ER model", topics: ["ER model", "Entities", "Relationships"] },
      { name: "Database Concepts : Relational model", topics: ["Relational model", "Tables", "Keys"] },
      { name: "Database Concepts : Normalization (1NF, 2NF, 3NF)", topics: ["Normalization", "Normal forms", "Decomposition"] },
      { name: "SQL : DDL, DML and DCL commands", topics: ["DDL", "DML", "DCL"] },
      { name: "SQL : Aggregate functions", topics: ["Aggregate functions", "SUM", "AVG", "COUNT"] },
      { name: "SQL : Joins and subqueries", topics: ["Joins", "Subqueries", "Types"] },
      { name: "SQL : Constraints and keys", topics: ["Constraints", "Primary key", "Foreign key"] },
      { name: "Network Fundamentals : OSI and TCP/IP models", topics: ["OSI model", "TCP/IP", "Layers"] },
      { name: "Network Fundamentals : IP addressing", topics: ["IP addressing", "IPv4", "IPv6"] },
      { name: "Network Fundamentals : Network devices", topics: ["Network devices", "Router", "Switch"] },
      { name: "Network Fundamentals : Routing and switching", topics: ["Routing", "Switching", "Protocols"] },
      { name: "Internet and Web Technologies : HTTP and HTTPS", topics: ["HTTP", "HTTPS", "Protocols"] },
      { name: "Internet and Web Technologies : HTML and CSS basics", topics: ["HTML", "CSS", "Web design"] },
      { name: "Internet and Web Technologies : Cyber security fundamentals", topics: ["Cyber security", "Threats", "Prevention"] },
      { name: "Internet and Web Technologies : Cloud computing basics", topics: ["Cloud computing", "Services", "Models"] },
      { name: "Security Concepts : Types of cyber threats", topics: ["Cyber threats", "Malware", "Attacks"] },
      { name: "Security Concepts : Encryption and cryptography basics", topics: ["Encryption", "Cryptography", "Algorithms"] },
      { name: "Security Concepts : Digital signatures", topics: ["Digital signatures", "PKI", "Authentication"] },
      { name: "Security Concepts : Ethical hacking principles", topics: ["Ethical hacking", "Penetration testing", "Security audits"] }
    ]
  },
  hindi: {
    title: "EMRS PGT Hindi Syllabus",
    sections: [
      { 
        name: "हिंदी साहित्य का उद्भव और विकास", 
        topics: [
          "अपभ्रंश से हिंदी का विकास",
          "आदिकाल की विशेषताएँ",
          "भक्तिकाल की प्रमुख प्रवृत्तियाँ",
          "रीतिकाल की विशेषताएँ",
          "आधुनिक काल का स्वरूप"
        ] 
      },
      { 
        name: "उपन्यास", 
        topics: [
          "हिंदी उपन्यास का विकास",
          "प्रेमचंद और यथार्थवाद",
          "मनोवैज्ञानिक उपन्यास",
          "प्रयोगवाद एवं नई कहानी आंदोलन",
          "प्रमुख उपन्यासकार एवं उनकी कृतियाँ"
        ] 
      },
      { 
        name: "कहानी", 
        topics: [
          "हिंदी कहानी का उद्भव और विकास",
          "नई कहानी आंदोलन",
          "कथानक, चरित्र चित्रण, संवाद",
          "प्रमुख कहानीकार एवं उनकी रचनाएँ"
        ] 
      },
      { 
        name: "नाटक और एकांकी", 
        topics: [
          "हिंदी नाटक का इतिहास",
          "भारतेन्दु युगीन नाटक",
          "जयशंकर प्रसाद के नाटक",
          "आधुनिक नाटककार एवं प्रवृत्तियाँ"
        ] 
      },
      { 
        name: "निबंध, संस्मरण और आत्मकथा", 
        topics: [
          "हिंदी निबंध का विकास",
          "संस्मरण की विशेषताएँ",
          "आत्मकथात्मक साहित्य",
          "हिंदी यात्रा साहित्य",
          "रिपोर्ट लेखन की शैली"
        ] 
      },
      { 
        name: "आदिकालीन काव्य", 
        topics: [
          "वीरगाथा काव्य",
          "प्रमुख कवि एवं कृतियाँ"
        ] 
      },
      { 
        name: "भक्तिकालीन काव्य", 
        topics: [
          "निर्गुण भक्ति धारा",
          "सगुण भक्ति धारा",
          "प्रमुख संत कवि – कबीर, सूरदास, तुलसीदास",
          "काव्य की प्रवृत्तियाँ"
        ] 
      },
      { 
        name: "रीतिकालीन काव्य", 
        topics: [
          "श्रृंगार रस की प्रधानता",
          "अलंकारिक शैली",
          "बिहारी, केशवदास, पद्माकर"
        ] 
      },
      { 
        name: "आधुनिक कालीन काव्य", 
        topics: [
          "भारतेंदु युग",
          "द्विवेदी युग",
          "छायावाद – प्रसाद, पंत, निराला, महादेवी",
          "प्रगतिवाद, प्रयोगवाद, नई कविता",
          "समकालीन कविता"
        ] 
      },
      { 
        name: "हिंदी भाषा का स्वरूप", 
        topics: [
          "भाषा की उत्पत्ति और विकास",
          "उपभाषाएँ और बोलियाँ",
          "राजभाषा एवं राष्ट्रभाषा"
        ] 
      },
      { 
        name: "व्याकरण", 
        topics: [
          "संधि, समास",
          "शब्द-रचना और वाक्य-रचना",
          "अलंकार, रस, छंद",
          "मुहावरे और लोकोक्तियाँ"
        ] 
      },
      { 
        name: "काव्यशास्त्र", 
        topics: [
          "रस सिद्धांत",
          "अलंकार सिद्धांत",
          "रीति सिद्धांत",
          "ध्वनि सिद्धांत"
        ] 
      },
      { 
        name: "हिंदी आलोचना", 
        topics: [
          "आचार्य रामचंद्र शुक्ल",
          "हजारी प्रसाद द्विवेदी",
          "नामवर सिंह",
          "आधुनिक आलोचना प्रवृत्तियाँ"
        ] 
      }
    ]
  },
  english: {
    title: "EMRS PGT English Syllabus (Post-Graduation Level)",
    sections: [
      { name: "Drama : Elizabethan Drama – William Shakespeare (Macbeth)", topics: ["Macbeth analysis", "Tragic hero", "Themes and motifs"] },
      { name: "Drama : Restoration Comedy – Oliver Goldsmith (She Stoops to Conquer)", topics: ["Comedy of manners", "Satire", "Character analysis"] },
      { name: "Drama : Modern Drama – G.B. Shaw (Arms and the Man)", topics: ["Anti-romantic comedy", "War and heroism", "Shavian wit"] },
      { name: "Drama : Verse Drama – T.S. Eliot (Murder in the Cathedral)", topics: ["Religious drama", "Verse technique", "Martyrdom theme"] },
      { name: "Drama : Dramatic Devices – Soliloquy, Aside, Catharsis, Tragic Hero", topics: ["Soliloquy", "Aside", "Catharsis", "Tragic hero"] },
      { name: "Poetry : Romantic Poetry – Wordsworth, Keats, Shelley", topics: ["Wordsworth's nature poetry", "Keats' odes", "Shelley's revolutionary spirit"] },
      { name: "Poetry : Victorian Poetry – Tennyson, Browning", topics: ["Tennyson's elegiac poetry", "Browning's dramatic monologue", "Victorian doubt"] },
      { name: "Poetry : Modern Poetry – T.S. Eliot, W.B. Yeats", topics: ["The Waste Land", "Yeats' symbolism", "Modernist techniques"] },
      { name: "Poetry : American Poetry – Walt Whitman", topics: ["Leaves of Grass", "Free verse", "Democratic ideals"] },
      { name: "Poetry : Poetic Devices – Imagery, Symbolism, Metaphor, Simile, Irony", topics: ["Imagery", "Symbolism", "Metaphor", "Simile", "Irony"] },
      { name: "Poetry : Forms – Sonnet, Ode, Elegy, Ballad, Epic", topics: ["Sonnet forms", "Ode structure", "Elegy", "Ballad", "Epic conventions"] },
      { name: "Prose : Essays – Francis Bacon, Charles Lamb", topics: ["Bacon's aphoristic style", "Lamb's personal essays", "Essay development"] },
      { name: "Prose : Periodical Essays – Addison & Steele", topics: ["The Spectator", "The Tatler", "18th century prose"] },
      { name: "Prose : Modern Essays – A.G. Gardiner", topics: ["Informal essay", "Gentle humor", "Modern prose style"] },
      { name: "Prose : Prose Style – Aphoristic, Personal, Didactic, Reflective", topics: ["Aphoristic style", "Personal essay", "Didactic prose", "Reflective writing"] },
      { name: "Fiction : Novel – Jane Austen", topics: ["Pride and Prejudice", "Social comedy", "Irony and wit"] },
      { name: "Fiction : Victorian Novel – Charles Dickens", topics: ["Great Expectations", "Social criticism", "Characterization"] },
      { name: "Fiction : Realism and Naturalism – Thomas Hardy", topics: ["Tess of the d'Urbervilles", "Wessex novels", "Fatalism"] },
      { name: "Fiction : Indian English Fiction – R.K. Narayan, Mulk Raj Anand", topics: ["Malgudi Days", "Untouchable", "Indian themes"] },
      { name: "Fiction : Narrative Techniques – Plot, Characterization, Point of View", topics: ["Plot structure", "Character development", "Narrative perspective"] },
      { name: "Literary Criticism : Classical Criticism – Aristotle (Poetics), Longinus", topics: ["Aristotle's Poetics", "Longinus on Sublime", "Classical concepts"] },
      { name: "Literary Criticism : Renaissance Criticism – Philip Sidney", topics: ["Defence of Poesy", "Renaissance ideals", "Poetry's function"] },
      { name: "Literary Criticism : Neo-Classical Criticism – John Dryden", topics: ["Essay of Dramatic Poesy", "Neo-classical rules", "Wit and judgment"] },
      { name: "Literary Criticism : Romantic Criticism – Wordsworth, Coleridge", topics: ["Preface to Lyrical Ballads", "Biographia Literaria", "Imagination theory"] },
      { name: "Literary Criticism : Modern Criticism – T.S. Eliot, I.A. Richards", topics: ["Tradition and Individual Talent", "Practical Criticism", "New Criticism"] },
      { name: "Literary Criticism : Concepts – Mimesis, Catharsis, Sublime, Objective Correlative", topics: ["Mimesis", "Catharsis", "Sublime", "Objective correlative"] },
      { name: "Indian Writing in English : Raja Rao", topics: ["Kanthapura", "Philosophical fiction", "Indian English style"] },
      { name: "Indian Writing in English : R.K. Narayan", topics: ["Malgudi fiction", "Humor and irony", "Indian middle class"] },
      { name: "Indian Writing in English : Mulk Raj Anand", topics: ["Social realism", "Untouchable", "Coolie"] },
      { name: "Indian Writing in English : Rabindranath Tagore", topics: ["Gitanjali", "Nobel Prize", "Philosophical poetry"] },
      { name: "Indian Writing in English : Sarojini Naidu", topics: ["Nightingale of India", "Patriotic poetry", "Lyrical verse"] },
      { name: "Indian Writing in English : Themes – Nationalism, Social Reform, Identity", topics: ["Nationalism in literature", "Social reform themes", "Identity quest"] },
      { name: "Linguistics : Phonetics and Phonology", topics: ["English phonemes", "Phonological rules", "Transcription"] },
      { name: "Linguistics : Morphology", topics: ["Word formation", "Affixes", "Morphological analysis"] },
      { name: "Linguistics : Syntax", topics: ["Sentence structure", "Phrase structure", "Transformations"] },
      { name: "Linguistics : Semantics", topics: ["Meaning in language", "Lexical semantics", "Pragmatics"] },
      { name: "Linguistics : Figures of Speech", topics: ["Metaphor", "Simile", "Personification", "Hyperbole"] },
      { name: "History of English Literature : Old English Period", topics: ["Beowulf", "Anglo-Saxon poetry", "Heroic tradition"] },
      { name: "History of English Literature : Middle English Period", topics: ["Chaucer", "Canterbury Tales", "Medieval romance"] },
      { name: "History of English Literature : Renaissance", topics: ["Shakespeare", "Spenser", "Elizabethan drama"] },
      { name: "History of English Literature : Neo-Classical Period", topics: ["Dryden", "Pope", "Augustan satire"] },
      { name: "History of English Literature : Romantic Period", topics: ["Romantic poets", "Nature poetry", "Imagination"] },
      { name: "History of English Literature : Victorian Period", topics: ["Victorian novel", "Social criticism", "Empire literature"] },
      { name: "History of English Literature : Modern and Postmodern Period", topics: ["Modernist experimentation", "Postmodern fiction", "Contemporary writing"] }
    ]
  },
  history: {
    title: "EMRS PGT History Syllabus",
    sections: [
      { 
        name: "Writing and City Life (Iraq, 3rd Millennium BCE)", 
        topics: [
          "Growth of towns",
          "Nature of early urban societies",
          "Uses of writing in early civilizations",
          "Historians' debate on the origins of writing"
        ] 
      },
      { 
        name: "An Empire Across Three Continents (Roman Empire, 27 BCE – 600 CE)", 
        topics: [
          "Political evolution of Roman Empire",
          "Economic expansion and trade",
          "Religion and cultural foundations",
          "Late Antiquity",
          "Institution of slavery – historians' views"
        ] 
      },
      { 
        name: "Nomadic Empires (The Mongols, 13th – 14th Century)", 
        topics: [
          "Nature of nomadism",
          "Formation of Mongol Empire",
          "Conquests and relations with other states",
          "Historians' views on nomadic societies and state formation"
        ] 
      },
      { 
        name: "The Three Orders (Western Europe, 13th – 16th Century)", 
        topics: [
          "Feudal society and economy",
          "Formation of state in medieval Europe",
          "Church and society",
          "Historians' views on decline of feudalism"
        ] 
      },
      { 
        name: "Changing Cultural Traditions (Europe, 14th – 17th Century)", 
        topics: [
          "Renaissance – new ideas in literature and arts",
          "Relationship with earlier classical traditions",
          "Contribution of West Asia",
          "Historians' viewpoint on 'European Renaissance'"
        ] 
      },
      { 
        name: "Displacing Indigenous People (North America & Australia, 18th – 20th Century)", 
        topics: [
          "European colonists in North America and Australia",
          "Formation of White Settler societies",
          "Displacement and repression of local people",
          "Historians' viewpoint on impact of European settlement"
        ] 
      },
      { 
        name: "Paths to Modernization (East Asia, Late 19th – 20th Century)", 
        topics: [
          "Militarization and economic growth in Japan",
          "China and the communist alternative",
          "Debate on meaning of modernization",
          "Historians' debate on development models"
        ] 
      }
    ]
  },
  political_science: {
    title: "EMRS PGT Political Science Syllabus",
    sections: [
      { name: "Political Theory", topics: ["State", "Sovereignty", "Justice", "Rights"] },
      { name: "Indian Constitution", topics: ["Making of Constitution", "Fundamental Rights", "DPSP"] }
    ]
  }
};
const nonTeachingSyllabusData = {
  hostel_warden: {
    title: "EMRS Hostel Warden Syllabus",
    sections: [
      {
        name: "General Knowledge & Current Affairs",
        topics: [
          "Indian History - Ancient, Medieval, Modern",
          "Indian Geography - Physical, Economic",
          "Indian Constitution - Fundamental Rights, Duties",
          "Current events - National, International",
          "Awards and honors",
          "Important dates and events"
        ]
      },
      {
        name: "Hostel Administration",
        topics: [
          "Roles and responsibilities of Hostel Warden",
          "Hostel rules and regulations",
          "Student admission and records management",
          "Daily routine management",
          "Staff supervision and coordination",
          "Emergency procedures"
        ]
      },
      {
        name: "Student Welfare & Guidance",
        topics: [
          "Understanding student psychology",
          "Counseling basics",
          "Handling student grievances",
          "Academic support and motivation",
          "Career guidance basics",
          "Parent communication"
        ]
      },
      {
        name: "Health & Hygiene",
        topics: [
          "Personal hygiene education",
          "Hostel cleanliness and sanitation",
          "Food safety and nutrition",
          "Common health issues in hostels",
          "First aid basics",
          "Medical emergency handling"
        ]
      },
      {
        name: "Safety & Security",
        topics: [
          "Hostel security measures",
          "Fire safety and evacuation drills",
          "Preventing ragging and bullying",
          "Night supervision duties",
          "Visitor management",
          "Crisis management"
        ]
      },
      {
        name: "Discipline & Behavior Management",
        topics: [
          "Maintaining discipline in hostel",
          "Handling misconduct",
          "Positive reinforcement techniques",
          "Conflict resolution",
          "Building positive hostel culture"
        ]
      },
      {
        name: "Mess & Food Management",
        topics: [
          "Menu planning and nutrition",
          "Mess management and supervision",
          "Food storage and hygiene",
          "Handling food complaints",
          "Budget management for mess"
        ]
      },
      {
        name: "Infrastructure & Maintenance",
        topics: [
          "Hostel facility management",
          "Maintenance and repairs coordination",
          "Inventory management",
          "Resource allocation",
          "Documentation and record keeping"
        ]
      },
      {
        name: "Legal & Administrative Knowledge",
        topics: [
          "Child protection laws",
          "POCSO Act awareness",
          "RTI basics",
          "Service rules and regulations",
          "Leave and attendance management"
        ]
      },
      {
        name: "Communication & Soft Skills",
        topics: [
          "Effective communication with students",
          "Report writing",
          "Official correspondence",
          "Team building",
          "Time management"
        ]
      }
    ]
  },
  library_science: {
    title: "EMRS Library Science Syllabus",
    sections: [
      {
        name: "Library & Information Science Foundations",
        topics: [
          "History and development of libraries in India",
          "Types of libraries - Academic, Public, Special, National",
          "Five Laws of Library Science by S.R. Ranganathan",
          "Library legislation in India",
          "Role of libraries in education and society",
          "National Library of India and its functions"
        ]
      },
      {
        name: "Library Classification",
        topics: [
          "Dewey Decimal Classification (DDC) - Structure and application",
          "Colon Classification (CC) by S.R. Ranganathan",
          "Universal Decimal Classification (UDC)",
          "Library of Congress Classification (LCC)",
          "Canons and principles of classification",
          "Notation systems and their types"
        ]
      },
      {
        name: "Library Cataloguing",
        topics: [
          "Anglo-American Cataloguing Rules (AACR2)",
          "Resource Description and Access (RDA)",
          "Classified Catalogue Code (CCC)",
          "MARC format and its applications",
          "Types of catalogues - Author, Title, Subject, Dictionary",
          "Online Public Access Catalogue (OPAC)"
        ]
      },
      {
        name: "Information Sources & Reference Services",
        topics: [
          "Primary, Secondary, and Tertiary sources of information",
          "Reference books - Encyclopedias, Dictionaries, Yearbooks",
          "Bibliographies and Indexes",
          "Government publications and reports",
          "Reference interview techniques",
          "Current awareness and SDI services"
        ]
      },
      {
        name: "Information Technology in Libraries",
        topics: [
          "Library automation - concepts and software (KOHA, SOUL)",
          "Digital library development",
          "Institutional repositories",
          "RFID technology in libraries",
          "Barcode and QR code systems",
          "Library management software"
        ]
      },
      {
        name: "Knowledge Organization",
        topics: [
          "Indexing - Pre-coordinate and Post-coordinate",
          "Abstracting services",
          "Thesaurus construction and usage",
          "Subject headings - Sears, LCSH",
          "Metadata standards - Dublin Core",
          "Ontologies and taxonomies"
        ]
      },
      {
        name: "Collection Development & Management",
        topics: [
          "Collection development policy",
          "Book selection principles and tools",
          "Acquisition procedures - Purchase, Gift, Exchange",
          "Weeding and stock verification",
          "Preservation and conservation of library materials",
          "Budget allocation and management"
        ]
      },
      {
        name: "Library Administration & Management",
        topics: [
          "Principles of library management",
          "Planning, Organizing, Staffing, Directing, Controlling",
          "Total Quality Management (TQM) in libraries",
          "Marketing of library services",
          "Library statistics and annual reports",
          "Performance evaluation of library services"
        ]
      },
      {
        name: "User Studies & Information Literacy",
        topics: [
          "Information needs and seeking behavior",
          "User studies methodologies",
          "Information literacy programs",
          "Reader guidance and advisory services",
          "Library orientation and instruction",
          "Bibliometric studies"
        ]
      },
      {
        name: "Networking & Resource Sharing",
        topics: [
          "Library networks - INFLIBNET, DELNET, NICNET",
          "Inter-library loan (ILL) services",
          "Resource sharing consortia",
          "National Knowledge Commission recommendations",
          "Open Access movement and repositories",
          "E-resources and digital subscriptions"
        ]
      }
    ]
  }
};

// TGT Syllabus Data - Expanded with 30+ topics per subject
const tgtSyllabusData = {
  hindi: {
    title: "EMRS TGT Hindi Syllabus (Graduation Level)",
    titleHindi: "हिंदी पाठ्यक्रम - स्नातक स्तर (TGT)",
    sections: [
      {
        name: "गद्य साहित्य : हिंदी गद्य का उद्भव और विकास (भारतेन्दु युग से आधुनिक युग तक)",
        topics: [
          "भारतेन्दु युग का गद्य साहित्य",
          "द्विवेदी युग का गद्य",
          "छायावाद युग का गद्य",
          "आधुनिक युग का गद्य साहित्य"
        ]
      },
      {
        name: "गद्य साहित्य : उपन्यास का विकास और प्रवृत्तियाँ (प्रेमचंद से उत्तर-आधुनिक उपन्यास तक)",
        topics: [
          "प्रेमचंद का उपन्यास साहित्य",
          "प्रेमचंदोत्तर उपन्यास",
          "नई कहानी आंदोलन",
          "उत्तर-आधुनिक उपन्यास"
        ]
      },
      {
        name: "गद्य साहित्य : यथार्थवाद और सामाजिक उपन्यास",
        topics: [
          "यथार्थवाद की अवधारणा",
          "सामाजिक उपन्यास की विशेषताएँ",
          "प्रमुख यथार्थवादी उपन्यासकार"
        ]
      },
      {
        name: "गद्य साहित्य : मनोवैज्ञानिक उपन्यास",
        topics: [
          "मनोविश्लेषणवाद का प्रभाव",
          "जैनेन्द्र कुमार के उपन्यास",
          "इलाचंद्र जोशी के उपन्यास"
        ]
      },
      {
        name: "गद्य साहित्य : आंचलिक उपन्यास",
        topics: [
          "आंचलिकता की अवधारणा",
          "फणीश्वरनाथ रेणु - मैला आंचल",
          "अन्य आंचलिक उपन्यासकार"
        ]
      },
      {
        name: "गद्य साहित्य : कहानी का विकास (प्रेमचंद, जैनेन्द्र, मोहन राकेश)",
        topics: [
          "प्रेमचंद की कहानियाँ",
          "जैनेन्द्र की कहानियाँ",
          "मोहन राकेश की कहानियाँ"
        ]
      },
      {
        name: "गद्य साहित्य : नई कहानी आंदोलन",
        topics: [
          "नई कहानी की विशेषताएँ",
          "प्रमुख नई कहानीकार",
          "नई कहानी की उपलब्धियाँ"
        ]
      },
      {
        name: "गद्य साहित्य : नाटक का विकास (भारतेन्दु, जयशंकर प्रसाद, मोहन राकेश)",
        topics: [
          "भारतेन्दु युगीन नाटक",
          "जयशंकर प्रसाद के नाटक",
          "मोहन राकेश के नाटक"
        ]
      },
      {
        name: "गद्य साहित्य : एकांकी नाटक और रंगमंच",
        topics: [
          "एकांकी का स्वरूप",
          "प्रमुख एकांकीकार",
          "हिंदी रंगमंच का विकास"
        ]
      },
      {
        name: "गद्य साहित्य : निबंध साहित्य (रामचंद्र शुक्ल, हजारीप्रसाद द्विवेदी)",
        topics: [
          "रामचंद्र शुक्ल के निबंध",
          "हजारीप्रसाद द्विवेदी के निबंध",
          "ललित निबंध परंपरा"
        ]
      },
      {
        name: "गद्य साहित्य : संस्मरण, आत्मकथा और जीवनी साहित्य",
        topics: [
          "संस्मरण साहित्य",
          "आत्मकथा साहित्य",
          "जीवनी साहित्य"
        ]
      },
      {
        name: "गद्य साहित्य : रेखाचित्र और यात्रावृत्त",
        topics: [
          "रेखाचित्र का स्वरूप",
          "यात्रावृत्त साहित्य",
          "प्रमुख रेखाचित्रकार"
        ]
      },
      {
        name: "काव्य भाग : आदिकालीन काव्य और वीरगाथा परंपरा",
        topics: [
          "आदिकाल का स्वरूप",
          "वीरगाथा काव्य",
          "रासो साहित्य"
        ]
      },
      {
        name: "काव्य भाग : भक्तिकाल – निर्गुण और सगुण धारा",
        topics: [
          "निर्गुण भक्ति धारा - कबीर, नानक",
          "सगुण राम भक्ति - तुलसीदास",
          "सगुण कृष्ण भक्ति - सूरदास, मीरा"
        ]
      },
      {
        name: "काव्य भाग : सूफी काव्य परंपरा",
        topics: [
          "सूफी काव्य की विशेषताएँ",
          "जायसी - पद्मावत",
          "अन्य सूफी कवि"
        ]
      },
      {
        name: "काव्य भाग : रीतिकालीन काव्य और अलंकारवाद",
        topics: [
          "रीतिकाल का स्वरूप",
          "रीतिबद्ध और रीतिसिद्ध कवि",
          "बिहारी, घनानंद, देव"
        ]
      },
      {
        name: "काव्य भाग : भारतेन्दु युगीन काव्य",
        topics: [
          "भारतेन्दु का काव्य",
          "युग की विशेषताएँ",
          "अन्य कवि"
        ]
      },
      {
        name: "काव्य भाग : द्विवेदी युगीन काव्य",
        topics: [
          "द्विवेदी युग की विशेषताएँ",
          "मैथिलीशरण गुप्त",
          "अन्य कवि"
        ]
      },
      {
        name: "काव्य भाग : छायावाद – प्रसाद, पंत, निराला, महादेवी",
        topics: [
          "जयशंकर प्रसाद का काव्य",
          "सुमित्रानंदन पंत",
          "सूर्यकांत त्रिपाठी निराला",
          "महादेवी वर्मा"
        ]
      },
      {
        name: "काव्य भाग : प्रगतिवाद और प्रयोगवाद",
        topics: [
          "प्रगतिवादी काव्य",
          "प्रयोगवादी काव्य",
          "तारसप्तक और नई कविता"
        ]
      },
      {
        name: "काव्य भाग : नई कविता और समकालीन कविता",
        topics: [
          "नई कविता की विशेषताएँ",
          "समकालीन कविता",
          "प्रमुख कवि"
        ]
      },
      {
        name: "काव्य भाग : आधुनिक हिंदी कविता में विमर्श (नारीवादी, दलित, आदिवासी)",
        topics: [
          "नारीवादी विमर्श",
          "दलित विमर्श",
          "आदिवासी विमर्श"
        ]
      },
      {
        name: "हिंदी भाषा का विकास एवं व्यावहारिक व्याकरण : हिंदी भाषा की उत्पत्ति और विकास",
        topics: [
          "हिंदी भाषा का इतिहास",
          "भाषा परिवार",
          "विकास के चरण"
        ]
      },
      {
        name: "हिंदी भाषा का विकास एवं व्यावहारिक व्याकरण : अपभ्रंश से खड़ी बोली तक विकास",
        topics: [
          "अपभ्रंश भाषा",
          "पुरानी हिंदी",
          "खड़ी बोली का विकास"
        ]
      },
      {
        name: "हिंदी भाषा का विकास एवं व्यावहारिक व्याकरण : ध्वनिविज्ञान और उच्चारण तंत्र",
        topics: [
          "स्वर और व्यंजन",
          "उच्चारण स्थान",
          "ध्वनि परिवर्तन"
        ]
      },
      {
        name: "हिंदी भाषा का विकास एवं व्यावहारिक व्याकरण : रूपविज्ञान (संज्ञा, सर्वनाम, क्रिया)",
        topics: [
          "संज्ञा के भेद",
          "सर्वनाम के भेद",
          "क्रिया के भेद"
        ]
      },
      {
        name: "हिंदी भाषा का विकास एवं व्यावहारिक व्याकरण : वाक्यविन्यास और वाक्य-रचना",
        topics: [
          "वाक्य के अंग",
          "वाक्य के भेद",
          "वाक्य परिवर्तन"
        ]
      },
      {
        name: "हिंदी भाषा का विकास एवं व्यावहारिक व्याकरण : अलंकार और रस सिद्धांत",
        topics: [
          "शब्दालंकार",
          "अर्थालंकार",
          "रस सिद्धांत"
        ]
      },
      {
        name: "हिंदी भाषा का विकास एवं व्यावहारिक व्याकरण : शब्द-शक्ति (अभिधा, लक्षणा, व्यंजना)",
        topics: [
          "अभिधा शब्द शक्ति",
          "लक्षणा शब्द शक्ति",
          "व्यंजना शब्द शक्ति"
        ]
      },
      {
        name: "हिंदी भाषा का विकास एवं व्यावहारिक व्याकरण : छंदशास्त्र",
        topics: [
          "मात्रिक छंद",
          "वर्णिक छंद",
          "मुक्त छंद"
        ]
      },
      {
        name: "हिंदी भाषा का विकास एवं व्यावहारिक व्याकरण : हिंदी का संवैधानिक स्वरूप और राजभाषा",
        topics: [
          "संविधान में हिंदी",
          "राजभाषा नीति",
          "हिंदी का प्रसार"
        ]
      }
    ]
  },
  english: {
    title: "EMRS TGT English Syllabus (Graduation Level)",
    sections: [
      {
        name: "Reading Comprehension : Literary theory and critical reading",
        topics: [
          "Critical reading strategies",
          "Literary theory foundations",
          "Textual analysis methods"
        ]
      },
      {
        name: "Reading Comprehension : Structural analysis of prose and poetry",
        topics: [
          "Prose structure analysis",
          "Poetry structure and form",
          "Comparative analysis"
        ]
      },
      {
        name: "Reading Comprehension : Rhetorical devices and stylistics",
        topics: [
          "Rhetorical devices",
          "Stylistic analysis",
          "Author's voice and tone"
        ]
      },
      {
        name: "Reading Comprehension : Discourse analysis",
        topics: [
          "Discourse theory",
          "Text and context",
          "Coherence and cohesion"
        ]
      },
      {
        name: "Reading Comprehension : Interpretative and inferential analysis",
        topics: [
          "Making inferences",
          "Interpretation strategies",
          "Critical evaluation"
        ]
      },
      {
        name: "Writing Ability : Academic writing and research methodology",
        topics: [
          "Academic writing conventions",
          "Research methodology",
          "Citation and referencing"
        ]
      },
      {
        name: "Writing Ability : Argumentative and analytical essays",
        topics: [
          "Argumentative essay structure",
          "Analytical essay writing",
          "Thesis development"
        ]
      },
      {
        name: "Writing Ability : Report writing and documentation",
        topics: [
          "Report writing formats",
          "Documentation styles",
          "Technical writing"
        ]
      },
      {
        name: "Writing Ability : Formal and professional correspondence",
        topics: [
          "Business letters",
          "Official correspondence",
          "Email etiquette"
        ]
      },
      {
        name: "Writing Ability : Creative writing and narrative techniques",
        topics: [
          "Narrative techniques",
          "Creative writing forms",
          "Story development"
        ]
      },
      {
        name: "Writing Ability : Speech and debate writing (persuasive rhetoric)",
        topics: [
          "Speech writing",
          "Debate structure",
          "Persuasive techniques"
        ]
      },
      {
        name: "Grammar and Usage : Advanced syntax and sentence structure",
        topics: [
          "Complex sentence structures",
          "Syntactic analysis",
          "Sentence transformation"
        ]
      },
      {
        name: "Grammar and Usage : Transformational grammar",
        topics: [
          "Chomsky's theory",
          "Deep and surface structure",
          "Transformation rules"
        ]
      },
      {
        name: "Grammar and Usage : Morphology and word formation",
        topics: [
          "Morphemes and affixes",
          "Word formation processes",
          "Derivation and inflection"
        ]
      },
      {
        name: "Grammar and Usage : Phonetics and phonology",
        topics: [
          "English phonemes",
          "Phonological rules",
          "Transcription"
        ]
      },
      {
        name: "Grammar and Usage : Semantics and pragmatics",
        topics: [
          "Meaning in language",
          "Speech acts",
          "Context and meaning"
        ]
      },
      {
        name: "Grammar and Usage : Error analysis and applied linguistics",
        topics: [
          "Common errors",
          "Error correction",
          "Applied linguistics"
        ]
      },
      {
        name: "Literature : Shakespearean Tragedy – Structure, Hamartia, Catharsis, Dramatic Irony",
        topics: [
          "Structure of Shakespearean tragedy",
          "Hamartia - tragic flaw",
          "Catharsis and dramatic irony",
          "Major tragedies: Hamlet, Macbeth, Othello, King Lear"
        ]
      },
      {
        name: "Literature : Shakespearean Comedy – Comic conventions, mistaken identity, festive structure",
        topics: [
          "Comic conventions",
          "Mistaken identity theme",
          "Festive structure",
          "A Midsummer Night's Dream, Twelfth Night"
        ]
      },
      {
        name: "Literature : Shakespearean History Plays – Political power and monarchy",
        topics: [
          "Tudor propaganda",
          "Divine right of kings",
          "Richard III, Henry IV, Henry V"
        ]
      },
      {
        name: "Literature : Elizabethan and Jacobean Drama – Marlowe, Webster, Ben Jonson",
        topics: [
          "Christopher Marlowe - Doctor Faustus",
          "John Webster - The Duchess of Malfi",
          "Ben Jonson - Volpone"
        ]
      },
      {
        name: "Literature : Metaphysical Poetry – John Donne, conceit, wit, paradox",
        topics: [
          "John Donne's poetry",
          "Metaphysical conceit",
          "Wit and paradox"
        ]
      },
      {
        name: "Literature : Neo-Classical Age – Dryden and Pope, satire and heroic couplet",
        topics: [
          "John Dryden",
          "Alexander Pope",
          "Satire and heroic couplet"
        ]
      },
      {
        name: "Literature : Romantic Poetry – Wordsworth's theory of imagination",
        topics: [
          "Preface to Lyrical Ballads",
          "Theory of imagination",
          "Nature poetry"
        ]
      },
      {
        name: "Literature : Romantic Poetry – Coleridge's supernaturalism",
        topics: [
          "Biographia Literaria",
          "The Rime of the Ancient Mariner",
          "Kubla Khan"
        ]
      },
      {
        name: "Literature : Romantic Poetry – Keats' negative capability",
        topics: [
          "Negative capability concept",
          "Ode on a Grecian Urn",
          "Ode to a Nightingale"
        ]
      },
      {
        name: "Literature : Victorian Poetry – Tennyson, Browning, dramatic monologue",
        topics: [
          "Alfred Lord Tennyson",
          "Robert Browning",
          "Dramatic monologue form"
        ]
      },
      {
        name: "Literature : Victorian Novel – Realism in Dickens and Hardy",
        topics: [
          "Charles Dickens",
          "Thomas Hardy",
          "Victorian realism"
        ]
      },
      {
        name: "Literature : Modernism – Stream of consciousness (James Joyce, Virginia Woolf)",
        topics: [
          "James Joyce - Ulysses",
          "Virginia Woolf - Mrs Dalloway",
          "Stream of consciousness technique"
        ]
      },
      {
        name: "Literature : Symbolism and Imagism – T.S. Eliot and Ezra Pound",
        topics: [
          "T.S. Eliot - The Waste Land",
          "Ezra Pound - Imagism",
          "Objective correlative"
        ]
      },
      {
        name: "Literature : Theatre of the Absurd – Samuel Beckett",
        topics: [
          "Waiting for Godot",
          "Absurdist philosophy",
          "Breakdown of communication"
        ]
      },
      {
        name: "Literature : American Literature – Transcendentalism (Emerson, Thoreau)",
        topics: [
          "Ralph Waldo Emerson",
          "Henry David Thoreau",
          "Transcendentalist philosophy"
        ]
      },
      {
        name: "Literature : American Realism and Naturalism – Hemingway, Faulkner",
        topics: [
          "Ernest Hemingway",
          "William Faulkner",
          "American realism"
        ]
      },
      {
        name: "Literature : Harlem Renaissance – Langston Hughes",
        topics: [
          "Langston Hughes",
          "African American literature",
          "Jazz poetry"
        ]
      },
      {
        name: "Literature : Postcolonial Literature – Edward Said, Orientalism",
        topics: [
          "Edward Said - Orientalism",
          "Postcolonial theory",
          "Colonial discourse"
        ]
      },
      {
        name: "Literature : Indian Writing in English – R.K. Narayan, Mulk Raj Anand",
        topics: [
          "R.K. Narayan - Malgudi Days",
          "Mulk Raj Anand - Untouchable",
          "Indian English literature"
        ]
      },
      {
        name: "Literature : Indian Postcolonial Novel – Salman Rushdie",
        topics: [
          "Midnight's Children",
          "Magic realism",
          "Postcolonial identity"
        ]
      },
      {
        name: "Literature : Feminist Literary Theory – Simone de Beauvoir, Elaine Showalter",
        topics: [
          "Simone de Beauvoir - The Second Sex",
          "Elaine Showalter - gynocriticism",
          "Feminist criticism"
        ]
      },
      {
        name: "Literature : Marxist Criticism – Class struggle and ideology",
        topics: [
          "Base and superstructure",
          "Ideology in literature",
          "Class struggle"
        ]
      },
      {
        name: "Literature : Structuralism – Ferdinand de Saussure",
        topics: [
          "Signifier and signified",
          "Language as system",
          "Binary oppositions"
        ]
      },
      {
        name: "Literature : Post-Structuralism – Derrida and Deconstruction",
        topics: [
          "Jacques Derrida",
          "Deconstruction",
          "Différance"
        ]
      },
      {
        name: "Literature : Reader Response Theory – Wolfgang Iser",
        topics: [
          "Wolfgang Iser",
          "Reader response theory",
          "Interpretive communities"
        ]
      },
      {
        name: "Literature : Psychoanalytic Criticism – Freud and Jung",
        topics: [
          "Freudian criticism",
          "Jungian archetypes",
          "Psychoanalysis in literature"
        ]
      },
      {
        name: "Literature : Ecocriticism and Environmental Literature",
        topics: [
          "Ecocriticism",
          "Nature writing",
          "Environmental ethics"
        ]
      },
      {
        name: "Literature : Comparative Literature and World Literature",
        topics: [
          "Comparative literature",
          "World literature",
          "Cross-cultural studies"
        ]
      },
      {
        name: "Literature : Literary Movements – Renaissance, Romanticism, Realism, Modernism, Postmodernism",
        topics: [
          "Renaissance literature",
          "Romanticism to Modernism",
          "Postmodernism"
        ]
      },
      {
        name: "Literature : Literary Genres – Epic, Novel, Drama, Poetry, Short Story",
        topics: [
          "Epic conventions",
          "Novel forms",
          "Drama and poetry"
        ]
      }
    ]
  },
  mathematics: {
    title: "EMRS TGT Mathematics Syllabus (Graduation Level)",
    sections: [
      {
        name: "Real Number : Structure of real number system (ℝ as a complete ordered field)",
        topics: [
          "Complete ordered field",
          "Axioms of real numbers",
          "Completeness property"
        ]
      },
      {
        name: "Real Number : Supremum and infimum property",
        topics: [
          "Least upper bound",
          "Greatest lower bound",
          "Bounded sets"
        ]
      },
      {
        name: "Real Number : Rational and irrational numbers (proof of irrationality)",
        topics: [
          "Proof of irrationality of √2",
          "Density of rationals",
          "Irrational numbers"
        ]
      },
      {
        name: "Real Number : Decimal expansion and convergence",
        topics: [
          "Decimal representation",
          "Recurring decimals",
          "Convergence of sequences"
        ]
      },
      {
        name: "Real Number : Euclid's division algorithm and Fundamental Theorem of Arithmetic",
        topics: [
          "Euclid's division lemma",
          "Fundamental theorem of arithmetic",
          "HCF and LCM"
        ]
      },
      {
        name: "Elementary Number Theory : Divisibility theory and modular arithmetic",
        topics: [
          "Divisibility rules",
          "Modular arithmetic",
          "Congruence relations"
        ]
      },
      {
        name: "Elementary Number Theory : Congruences and residue classes",
        topics: [
          "Linear congruences",
          "Residue classes",
          "Complete residue systems"
        ]
      },
      {
        name: "Elementary Number Theory : Chinese Remainder Theorem",
        topics: [
          "Statement and proof",
          "Applications",
          "Solving simultaneous congruences"
        ]
      },
      {
        name: "Elementary Number Theory : Fermat's Little Theorem",
        topics: [
          "Statement and proof",
          "Applications",
          "Primality testing"
        ]
      },
      {
        name: "Elementary Number Theory : Euler's Totient Function",
        topics: [
          "Definition and properties",
          "Euler's theorem",
          "Applications in cryptography"
        ]
      },
      {
        name: "Elementary Number Theory : Wilson's Theorem",
        topics: [
          "Statement and proof",
          "Applications",
          "Prime number characterization"
        ]
      },
      {
        name: "Matrices : Vector spaces over ℝ",
        topics: [
          "Vector space axioms",
          "Subspaces",
          "Linear combinations"
        ]
      },
      {
        name: "Matrices : Linear independence and basis",
        topics: [
          "Linear independence",
          "Basis and dimension",
          "Spanning sets"
        ]
      },
      {
        name: "Matrices : Rank and nullity theorem",
        topics: [
          "Rank of matrix",
          "Nullity",
          "Rank-nullity theorem"
        ]
      },
      {
        name: "Matrices : Eigenvalues and eigenvectors",
        topics: [
          "Characteristic equation",
          "Eigenvalues",
          "Eigenvectors"
        ]
      },
      {
        name: "Matrices : Diagonalization of matrices",
        topics: [
          "Diagonalizable matrices",
          "Similar matrices",
          "Applications"
        ]
      },
      {
        name: "Matrices : Linear transformations and matrix representation",
        topics: [
          "Linear transformations",
          "Matrix representation",
          "Kernel and image"
        ]
      },
      {
        name: "Matrices : Systems of linear equations (Gaussian elimination)",
        topics: [
          "Gaussian elimination",
          "Row echelon form",
          "Solution of systems"
        ]
      },
      {
        name: "Polynomials : Polynomial functions over real and complex fields",
        topics: [
          "Polynomial rings",
          "Real polynomials",
          "Complex polynomials"
        ]
      },
      {
        name: "Polynomials : Fundamental Theorem of Algebra",
        topics: [
          "Statement",
          "Implications",
          "Complex roots"
        ]
      },
      {
        name: "Polynomials : Factor theorem and remainder theorem",
        topics: [
          "Factor theorem",
          "Remainder theorem",
          "Applications"
        ]
      },
      {
        name: "Polynomials : Roots and multiplicity",
        topics: [
          "Root finding",
          "Multiplicity of roots",
          "Repeated roots"
        ]
      },
      {
        name: "Polynomials : Symmetric functions of roots",
        topics: [
          "Vieta's formulas",
          "Symmetric polynomials",
          "Newton's identities"
        ]
      },
      {
        name: "Linear Equations in Two Variables : Analytical geometry of straight lines",
        topics: [
          "Equation of line",
          "Distance and angle",
          "Parallel and perpendicular lines"
        ]
      },
      {
        name: "Linear Equations in Two Variables : Matrix method of solving systems",
        topics: [
          "Matrix method",
          "Cramer's rule",
          "Consistency conditions"
        ]
      },
      {
        name: "Linear Equations in Two Variables : Consistency conditions",
        topics: [
          "Consistent systems",
          "Inconsistent systems",
          "Infinite solutions"
        ]
      },
      {
        name: "Quadratic Equations : Nature of roots using discriminant",
        topics: [
          "Discriminant",
          "Nature of roots",
          "Real and complex roots"
        ]
      },
      {
        name: "Quadratic Equations : Complex roots and Argand plane",
        topics: [
          "Complex numbers",
          "Argand plane",
          "Conjugate roots"
        ]
      },
      {
        name: "Quadratic Equations : Transformation of equations",
        topics: [
          "Transformation of roots",
          "Formation of equations",
          "Applications"
        ]
      },
      {
        name: "Arithmetic Progressions : Sequences and series",
        topics: [
          "AP, GP, HP",
          "Sum of series",
          "Applications"
        ]
      },
      {
        name: "Arithmetic Progressions : Convergence of sequences",
        topics: [
          "Convergent sequences",
          "Limit of sequence",
          "Cauchy sequences"
        ]
      },
      {
        name: "Arithmetic Progressions : Sigma notation and summation formulas",
        topics: [
          "Sigma notation",
          "Summation formulas",
          "Telescoping series"
        ]
      },
      {
        name: "Inequalities : Algebraic inequalities",
        topics: [
          "Linear inequalities",
          "Quadratic inequalities",
          "Polynomial inequalities"
        ]
      },
      {
        name: "Inequalities : Cauchy–Schwarz inequality",
        topics: [
          "Statement and proof",
          "Applications",
          "Generalizations"
        ]
      },
      {
        name: "Inequalities : AM–GM inequality",
        topics: [
          "AM-GM inequality",
          "Weighted AM-GM",
          "Applications"
        ]
      },
      {
        name: "Inequalities : Absolute value inequalities",
        topics: [
          "Modulus function",
          "Triangle inequality",
          "Solving absolute value inequalities"
        ]
      },
      {
        name: "Calculus : Limits and continuity (ε–δ definition)",
        topics: [
          "Epsilon-delta definition",
          "Limits",
          "Continuity"
        ]
      },
      {
        name: "Calculus : Differentiability and Mean Value Theorems",
        topics: [
          "Differentiability",
          "Rolle's theorem",
          "Mean value theorem"
        ]
      },
      {
        name: "Calculus : Partial derivatives",
        topics: [
          "Partial differentiation",
          "Chain rule",
          "Total derivative"
        ]
      },
      {
        name: "Calculus : Indefinite and definite integrals",
        topics: [
          "Integration techniques",
          "Definite integrals",
          "Fundamental theorem of calculus"
        ]
      },
      {
        name: "Calculus : Applications of integration",
        topics: [
          "Area under curves",
          "Volume of revolution",
          "Applications"
        ]
      },
      {
        name: "Calculus : Differential equations (first order)",
        topics: [
          "First order ODEs",
          "Separable equations",
          "Linear equations"
        ]
      },
      {
        name: "Euclidean Geometry : Axiomatic method",
        topics: [
          "Euclid's axioms",
          "Postulates",
          "Logical structure"
        ]
      },
      {
        name: "Euclidean Geometry : Similarity and congruence",
        topics: [
          "Congruence criteria",
          "Similarity criteria",
          "Applications"
        ]
      },
      {
        name: "Euclidean Geometry : Ceva's and Menelaus Theorems",
        topics: [
          "Ceva's theorem",
          "Menelaus theorem",
          "Applications"
        ]
      },
      {
        name: "Euclidean Geometry : Nine-point circle",
        topics: [
          "Nine-point circle",
          "Properties",
          "Euler line"
        ]
      },
      {
        name: "Euclidean Geometry : Ptolemy's Theorem",
        topics: [
          "Ptolemy's theorem",
          "Cyclic quadrilaterals",
          "Applications"
        ]
      },
      {
        name: "Coordinate Geometry : Straight lines and pair of lines",
        topics: [
          "Equations of lines",
          "Pair of lines",
          "Angle between lines"
        ]
      },
      {
        name: "Coordinate Geometry : Circle and system of circles",
        topics: [
          "Equation of circle",
          "System of circles",
          "Radical axis"
        ]
      },
      {
        name: "Coordinate Geometry : Parabola, ellipse and hyperbola",
        topics: [
          "Conic sections",
          "Standard forms",
          "Properties"
        ]
      },
      {
        name: "Coordinate Geometry : Three-dimensional geometry basics",
        topics: [
          "3D coordinates",
          "Direction cosines",
          "Lines and planes"
        ]
      },
      {
        name: "Statistics : Measures of central tendency",
        topics: [
          "Mean, median, mode",
          "Weighted averages",
          "Applications"
        ]
      },
      {
        name: "Statistics : Probability distributions (Binomial, Poisson, Normal)",
        topics: [
          "Binomial distribution",
          "Poisson distribution",
          "Normal distribution"
        ]
      },
      {
        name: "Statistics : Mathematical expectation and variance",
        topics: [
          "Expected value",
          "Variance",
          "Standard deviation"
        ]
      },
      {
        name: "Statistics : Correlation and regression",
        topics: [
          "Correlation coefficient",
          "Linear regression",
          "Applications"
        ]
      },
      {
        name: "Statistics : Hypothesis testing",
        topics: [
          "Null hypothesis",
          "Significance testing",
          "p-values"
        ]
      },
      {
        name: "Probability : Axiomatic probability theory",
        topics: [
          "Axioms of probability",
          "Sample space",
          "Events"
        ]
      },
      {
        name: "Probability : Conditional probability",
        topics: [
          "Conditional probability",
          "Independent events",
          "Applications"
        ]
      },
      {
        name: "Probability : Bayes' theorem",
        topics: [
          "Bayes' theorem",
          "Prior and posterior",
          "Applications"
        ]
      },
      {
        name: "Probability : Random variables and distribution",
        topics: [
          "Random variables",
          "Probability distributions",
          "CDF and PDF"
        ]
      }
    ]
  },
  science: {
    title: "EMRS TGT Science Syllabus (Graduation Level)",
    sections: [
      {
        name: "Effect of Current : Electric charge and Coulomb's Law",
        topics: [
          "Electric charge properties",
          "Coulomb's Law",
          "Superposition principle"
        ]
      },
      {
        name: "Effect of Current : Electric field intensity and electric field lines",
        topics: [
          "Electric field definition",
          "Field lines properties",
          "Field due to point charges"
        ]
      },
      {
        name: "Effect of Current : Electric potential and potential difference",
        topics: [
          "Electric potential",
          "Potential difference",
          "Equipotential surfaces"
        ]
      },
      {
        name: "Effect of Current : Gauss's Law (conceptual framework)",
        topics: [
          "Gauss's law statement",
          "Electric flux",
          "Applications"
        ]
      },
      {
        name: "Effect of Current : Capacitance and dielectric properties",
        topics: [
          "Capacitance",
          "Parallel plate capacitor",
          "Dielectric materials"
        ]
      },
      {
        name: "Effect of Current : Current density, drift velocity and mobility",
        topics: [
          "Current density",
          "Drift velocity",
          "Electron mobility"
        ]
      },
      {
        name: "Effect of Current : Ohm's Law (microscopic and macroscopic form)",
        topics: [
          "Ohm's law",
          "Resistance and resistivity",
          "Temperature dependence"
        ]
      },
      {
        name: "Effect of Current : Kirchhoff's Laws and network analysis",
        topics: [
          "Kirchhoff's current law",
          "Kirchhoff's voltage law",
          "Network analysis"
        ]
      },
      {
        name: "Effect of Current : Magnetic field due to current (Biot–Savart Law)",
        topics: [
          "Biot-Savart law",
          "Magnetic field of wire",
          "Applications"
        ]
      },
      {
        name: "Effect of Current : Ampere's circuital law",
        topics: [
          "Ampere's law",
          "Applications",
          "Solenoid and toroid"
        ]
      },
      {
        name: "Effect of Current : Electromagnetic induction (Faraday & Lenz Law)",
        topics: [
          "Faraday's law",
          "Lenz's law",
          "Induced EMF"
        ]
      },
      {
        name: "Effect of Current : Self and mutual inductance",
        topics: [
          "Self inductance",
          "Mutual inductance",
          "Energy in inductor"
        ]
      },
      {
        name: "Effect of Current : Alternating current, impedance and resonance",
        topics: [
          "AC circuits",
          "Impedance",
          "Resonance"
        ]
      },
      {
        name: "Effect of Current : Working principle of electric motor and generator",
        topics: [
          "DC motor",
          "AC generator",
          "Electromagnetic conversion"
        ]
      },
      {
        name: "Light : Laws of reflection and refraction",
        topics: [
          "Laws of reflection",
          "Snell's law",
          "Refractive index"
        ]
      },
      {
        name: "Light : Refraction at spherical surfaces",
        topics: [
          "Refraction at curved surfaces",
          "Lens maker's equation",
          "Power of lens"
        ]
      },
      {
        name: "Light : Thin lens formula and lens maker's equation",
        topics: [
          "Thin lens formula",
          "Lens combinations",
          "Magnification"
        ]
      },
      {
        name: "Light : Optical fiber and total internal reflection",
        topics: [
          "Total internal reflection",
          "Critical angle",
          "Optical fiber communication"
        ]
      },
      {
        name: "Light : Dispersion, scattering and Rayleigh theory",
        topics: [
          "Dispersion of light",
          "Rayleigh scattering",
          "Blue sky and red sunset"
        ]
      },
      {
        name: "Light : Interference and diffraction (wave nature of light)",
        topics: [
          "Young's double slit",
          "Diffraction patterns",
          "Wave optics"
        ]
      },
      {
        name: "Light : Polarization of light",
        topics: [
          "Polarization types",
          "Malus law",
          "Polaroids"
        ]
      },
      {
        name: "Light : Optical instruments (microscope and telescope)",
        topics: [
          "Simple microscope",
          "Compound microscope",
          "Telescope"
        ]
      },
      {
        name: "Light : Human eye and optical defects",
        topics: [
          "Structure of eye",
          "Myopia and hypermetropia",
          "Presbyopia and astigmatism"
        ]
      },
      {
        name: "Sources of Energy : Laws of thermodynamics",
        topics: [
          "First law",
          "Second law",
          "Entropy"
        ]
      },
      {
        name: "Sources of Energy : Fossil fuels and calorific value analysis",
        topics: [
          "Types of fossil fuels",
          "Calorific value",
          "Combustion analysis"
        ]
      },
      {
        name: "Sources of Energy : Thermal and hydroelectric power plants",
        topics: [
          "Thermal power plant",
          "Hydroelectric power",
          "Efficiency"
        ]
      },
      {
        name: "Sources of Energy : Solar cell and photovoltaic effect",
        topics: [
          "Photovoltaic effect",
          "Solar cell working",
          "Applications"
        ]
      },
      {
        name: "Sources of Energy : Wind, tidal and geothermal energy",
        topics: [
          "Wind energy",
          "Tidal energy",
          "Geothermal energy"
        ]
      },
      {
        name: "Sources of Energy : Nuclear fission and fusion reactions",
        topics: [
          "Nuclear fission",
          "Nuclear fusion",
          "Chain reaction"
        ]
      },
      {
        name: "Sources of Energy : Radioactivity and mass–energy equivalence",
        topics: [
          "Radioactive decay",
          "E = mc²",
          "Half-life"
        ]
      },
      {
        name: "Sources of Energy : Energy sustainability and environmental impact",
        topics: [
          "Sustainable energy",
          "Environmental impact",
          "Carbon footprint"
        ]
      },
      {
        name: "Motion: Force and Newton's Laws : Kinematics in one and two dimensions",
        topics: [
          "1D kinematics",
          "Projectile motion",
          "Relative motion"
        ]
      },
      {
        name: "Motion: Force and Newton's Laws : Vector mechanics",
        topics: [
          "Vector operations",
          "Resolution of vectors",
          "Applications"
        ]
      },
      {
        name: "Motion: Force and Newton's Laws : Newton's laws in inertial and non-inertial frames",
        topics: [
          "Newton's three laws",
          "Inertial frames",
          "Pseudo forces"
        ]
      },
      {
        name: "Motion: Force and Newton's Laws : Work-energy theorem",
        topics: [
          "Work-energy theorem",
          "Kinetic energy",
          "Applications"
        ]
      },
      {
        name: "Motion: Force and Newton's Laws : Linear momentum and conservation laws",
        topics: [
          "Linear momentum",
          "Conservation of momentum",
          "Collisions"
        ]
      },
      {
        name: "Motion: Force and Newton's Laws : Rotational dynamics and torque",
        topics: [
          "Torque",
          "Angular momentum",
          "Moment of inertia"
        ]
      },
      {
        name: "Gravitation: Work, Energy and Power : Universal law of gravitation",
        topics: [
          "Newton's law of gravitation",
          "Gravitational constant",
          "Gravitational force"
        ]
      },
      {
        name: "Gravitation: Work, Energy and Power : Gravitational potential and field",
        topics: [
          "Gravitational field",
          "Gravitational potential",
          "Potential energy"
        ]
      },
      {
        name: "Gravitation: Work, Energy and Power : Satellite motion and orbital mechanics",
        topics: [
          "Orbital velocity",
          "Escape velocity",
          "Satellite motion"
        ]
      },
      {
        name: "Gravitation: Work, Energy and Power : Conservative forces",
        topics: [
          "Conservative forces",
          "Path independence",
          "Potential energy"
        ]
      },
      {
        name: "Gravitation: Work, Energy and Power : Mechanical energy conservation",
        topics: [
          "Conservation of energy",
          "Work done by gravity",
          "Applications"
        ]
      },
      {
        name: "Gravitation: Work, Energy and Power : Power and efficiency",
        topics: [
          "Power definition",
          "Efficiency",
          "Applications"
        ]
      },
      {
        name: "Matter – Nature and Behaviour : Intermolecular forces",
        topics: [
          "Van der Waals forces",
          "Hydrogen bonding",
          "Ionic and covalent bonds"
        ]
      },
      {
        name: "Matter – Nature and Behaviour : Kinetic theory of gases",
        topics: [
          "Kinetic theory assumptions",
          "Gas laws",
          "Maxwell distribution"
        ]
      },
      {
        name: "Matter – Nature and Behaviour : Thermodynamic properties of matter",
        topics: [
          "Internal energy",
          "Enthalpy",
          "Specific heat"
        ]
      },
      {
        name: "Matter – Nature and Behaviour : Phase transitions",
        topics: [
          "States of matter",
          "Phase diagrams",
          "Latent heat"
        ]
      },
      {
        name: "Matter – Nature and Behaviour : Colloids and surface chemistry",
        topics: [
          "Colloids classification",
          "Surface tension",
          "Adsorption"
        ]
      },
      {
        name: "Structure of Atom : Atomic models (Thomson, Rutherford, Bohr)",
        topics: [
          "Thomson model",
          "Rutherford model",
          "Bohr model"
        ]
      },
      {
        name: "Structure of Atom : Quantum numbers and atomic orbitals",
        topics: [
          "Four quantum numbers",
          "Atomic orbitals",
          "Orbital shapes"
        ]
      },
      {
        name: "Structure of Atom : Heisenberg uncertainty principle",
        topics: [
          "Uncertainty principle",
          "Wave-particle duality",
          "Implications"
        ]
      },
      {
        name: "Structure of Atom : Electronic configuration and Aufbau principle",
        topics: [
          "Aufbau principle",
          "Pauli exclusion",
          "Hund's rule"
        ]
      },
      {
        name: "Structure of Atom : Nuclear stability and radioactivity",
        topics: [
          "Nuclear stability",
          "Binding energy",
          "Radioactive decay"
        ]
      },
      {
        name: "Chemical Reactions : Stoichiometry and mole concept",
        topics: [
          "Mole concept",
          "Stoichiometric calculations",
          "Limiting reagent"
        ]
      },
      {
        name: "Chemical Reactions : Thermochemistry",
        topics: [
          "Enthalpy changes",
          "Hess's law",
          "Bond energies"
        ]
      },
      {
        name: "Chemical Reactions : Chemical equilibrium",
        topics: [
          "Equilibrium constant",
          "Le Chatelier's principle",
          "Applications"
        ]
      },
      {
        name: "Chemical Reactions : Redox reactions and electrochemistry",
        topics: [
          "Oxidation-reduction",
          "Electrochemical cells",
          "Electrolysis"
        ]
      },
      {
        name: "Chemical Reactions : Reaction kinetics",
        topics: [
          "Rate of reaction",
          "Rate laws",
          "Activation energy"
        ]
      },
      {
        name: "Carbon Compounds : Hybridization and molecular geometry",
        topics: [
          "sp, sp², sp³ hybridization",
          "VSEPR theory",
          "Molecular shapes"
        ]
      },
      {
        name: "Carbon Compounds : Isomerism (structural & stereoisomerism)",
        topics: [
          "Structural isomerism",
          "Geometric isomerism",
          "Optical isomerism"
        ]
      },
      {
        name: "Carbon Compounds : Reaction mechanisms (SN1, SN2, E1, E2)",
        topics: [
          "Nucleophilic substitution",
          "Elimination reactions",
          "Mechanism comparison"
        ]
      },
      {
        name: "Carbon Compounds : Aromaticity and electrophilic substitution",
        topics: [
          "Aromaticity rules",
          "Benzene structure",
          "Electrophilic substitution"
        ]
      },
      {
        name: "Carbon Compounds : Functional group chemistry",
        topics: [
          "Alcohols and ethers",
          "Aldehydes and ketones",
          "Carboxylic acids"
        ]
      },
      {
        name: "Life Processes : Cell structure and ultrastructure",
        topics: [
          "Cell organelles",
          "Membrane structure",
          "Prokaryotes vs eukaryotes"
        ]
      },
      {
        name: "Life Processes : Biomolecules (proteins, carbohydrates, lipids)",
        topics: [
          "Protein structure",
          "Carbohydrate types",
          "Lipid functions"
        ]
      },
      {
        name: "Life Processes : Enzyme kinetics",
        topics: [
          "Enzyme mechanism",
          "Michaelis-Menten kinetics",
          "Enzyme inhibition"
        ]
      },
      {
        name: "Life Processes : Cellular respiration",
        topics: [
          "Glycolysis",
          "Krebs cycle",
          "Electron transport chain"
        ]
      },
      {
        name: "Life Processes : Photosynthesis (biochemical pathways)",
        topics: [
          "Light reactions",
          "Calvin cycle",
          "C3 and C4 pathways"
        ]
      },
      {
        name: "Heredity and Evolution : Mendelian genetics",
        topics: [
          "Mendel's laws",
          "Monohybrid cross",
          "Dihybrid cross"
        ]
      },
      {
        name: "Heredity and Evolution : Chromosomal theory of inheritance",
        topics: [
          "Chromosome structure",
          "Sex determination",
          "Linkage"
        ]
      },
      {
        name: "Heredity and Evolution : Molecular genetics (DNA replication & transcription)",
        topics: [
          "DNA replication",
          "Transcription",
          "Translation"
        ]
      },
      {
        name: "Heredity and Evolution : Mutation and genetic variation",
        topics: [
          "Types of mutations",
          "Genetic variation",
          "Mutation effects"
        ]
      },
      {
        name: "Heredity and Evolution : Darwinian evolution and modern synthesis",
        topics: [
          "Natural selection",
          "Modern synthesis",
          "Evidence of evolution"
        ]
      }
    ]
  },
  social_science: {
    title: "EMRS TGT Social Science Syllabus (Graduation Level)",
    sections: [
      {
        name: "Contemporary World : Industrial Revolution – causes, phases and global impact",
        topics: [
          "Causes of Industrial Revolution",
          "Phases of industrialization",
          "Global impact"
        ]
      },
      {
        name: "Contemporary World : Capitalism and emergence of industrial society",
        topics: [
          "Rise of capitalism",
          "Industrial society",
          "Social changes"
        ]
      },
      {
        name: "Contemporary World : Economic Depression of 1929 – global consequences",
        topics: [
          "Causes of depression",
          "Global consequences",
          "Recovery measures"
        ]
      },
      {
        name: "Contemporary World : Labour movements and class struggle",
        topics: [
          "Labour movements",
          "Trade unions",
          "Class struggle"
        ]
      },
      {
        name: "Contemporary World : Colonial economy and industrial growth in India",
        topics: [
          "Colonial economy",
          "Drain of wealth",
          "De-industrialization"
        ]
      },
      {
        name: "French Revolution : Enlightenment philosophy (Rousseau, Voltaire, Montesquieu)",
        topics: [
          "Enlightenment thinkers",
          "Social contract theory",
          "Separation of powers"
        ]
      },
      {
        name: "French Revolution : Socio-economic causes of revolution",
        topics: [
          "Social causes",
          "Economic crisis",
          "Fiscal problems"
        ]
      },
      {
        name: "French Revolution : Political restructuring and constitutional monarchy",
        topics: [
          "Political changes",
          "Constitutional monarchy",
          "National Assembly"
        ]
      },
      {
        name: "French Revolution : Reign of Terror and radical phase",
        topics: [
          "Reign of Terror",
          "Jacobins",
          "Robespierre"
        ]
      },
      {
        name: "French Revolution : Impact on Europe and rise of nationalism",
        topics: [
          "Impact on Europe",
          "Napoleonic era",
          "Rise of nationalism"
        ]
      },
      {
        name: "The Revolt of 1857 : Causes – political, economic and military",
        topics: [
          "Political causes",
          "Economic causes",
          "Military causes"
        ]
      },
      {
        name: "The Revolt of 1857 : Nature of revolt (war of independence debate)",
        topics: [
          "Nature of revolt",
          "War of independence debate",
          "Historiographical perspectives"
        ]
      },
      {
        name: "The Revolt of 1857 : Regional centers and leadership",
        topics: [
          "Regional centers",
          "Leaders of revolt",
          "Popular participation"
        ]
      },
      {
        name: "The Revolt of 1857 : Consequences and administrative reforms",
        topics: [
          "Consequences",
          "Administrative changes",
          "End of Company rule"
        ]
      },
      {
        name: "Indian Freedom Struggle – 1885 to 1947 : Moderate and extremist phases",
        topics: [
          "Moderate phase",
          "Extremist phase",
          "Swadeshi movement"
        ]
      },
      {
        name: "Indian Freedom Struggle – 1885 to 1947 : Gandhian movements",
        topics: [
          "Non-cooperation",
          "Civil disobedience",
          "Quit India"
        ]
      },
      {
        name: "Indian Freedom Struggle – 1885 to 1947 : Revolutionary nationalism",
        topics: [
          "Revolutionary movements",
          "Bhagat Singh",
          "INA and Subhas Bose"
        ]
      },
      {
        name: "Indian Freedom Struggle – 1885 to 1947 : Constitutional developments",
        topics: [
          "Constitutional reforms",
          "Government of India Acts",
          "Constituent Assembly"
        ]
      },
      {
        name: "Indian Freedom Struggle – 1885 to 1947 : Partition and independence",
        topics: [
          "Two-nation theory",
          "Partition",
          "Independence"
        ]
      },
      {
        name: "Russian Revolution : Marxist ideology and socialism",
        topics: [
          "Marxism",
          "Socialism",
          "Class struggle"
        ]
      },
      {
        name: "Russian Revolution : 1905 Revolution",
        topics: [
          "Bloody Sunday",
          "1905 Revolution",
          "October Manifesto"
        ]
      },
      {
        name: "Russian Revolution : February and October Revolution (1917)",
        topics: [
          "February Revolution",
          "October Revolution",
          "Bolshevik takeover"
        ]
      },
      {
        name: "Russian Revolution : Bolshevik consolidation of power",
        topics: [
          "Lenin's policies",
          "Civil war",
          "War communism"
        ]
      },
      {
        name: "Russian Revolution : Impact on global politics",
        topics: [
          "Global impact",
          "Communist movements",
          "Cold War origins"
        ]
      },
      {
        name: "The Two World Wars and the Establishment of UN : Causes of WWI",
        topics: [
          "Alliance system",
          "Nationalism",
          "Assassination of Archduke"
        ]
      },
      {
        name: "The Two World Wars and the Establishment of UN : Treaty of Versailles",
        topics: [
          "Peace settlement",
          "Treaty terms",
          "Consequences"
        ]
      },
      {
        name: "The Two World Wars and the Establishment of UN : Causes of WWII",
        topics: [
          "Rise of fascism",
          "Appeasement policy",
          "German expansion"
        ]
      },
      {
        name: "The Two World Wars and the Establishment of UN : Cold War origins",
        topics: [
          "Post-war tensions",
          "Iron Curtain",
          "Bipolar world"
        ]
      },
      {
        name: "The Two World Wars and the Establishment of UN : Formation and structure of United Nations",
        topics: [
          "UN formation",
          "Structure",
          "Functions"
        ]
      },
      {
        name: "Introduction to Solar System : Origin of universe (Big Bang theory)",
        topics: [
          "Big Bang theory",
          "Formation of galaxies",
          "Cosmic evolution"
        ]
      },
      {
        name: "Introduction to Solar System : Formation of solar system (nebular hypothesis)",
        topics: [
          "Nebular hypothesis",
          "Planet formation",
          "Solar system structure"
        ]
      },
      {
        name: "Introduction to Solar System : Structure of Earth",
        topics: [
          "Earth's layers",
          "Internal structure",
          "Composition"
        ]
      },
      {
        name: "Earth's Interior : Structure of Earth (crust, mantle, core)",
        topics: [
          "Crust composition",
          "Mantle characteristics",
          "Core structure"
        ]
      },
      {
        name: "Earth's Interior : Plate tectonics theory",
        topics: [
          "Plate tectonics",
          "Continental drift",
          "Sea floor spreading"
        ]
      },
      {
        name: "Earth's Interior : Earthquakes and seismic waves",
        topics: [
          "Earthquake causes",
          "Seismic waves",
          "Measurement"
        ]
      },
      {
        name: "Earth's Interior : Volcanoes and mountain building",
        topics: [
          "Volcanic activity",
          "Mountain formation",
          "Orogenesis"
        ]
      },
      {
        name: "Climate : Atmospheric composition and structure",
        topics: [
          "Atmospheric layers",
          "Composition",
          "Properties"
        ]
      },
      {
        name: "Climate : Heat budget and temperature distribution",
        topics: [
          "Heat budget",
          "Temperature distribution",
          "Insolation"
        ]
      },
      {
        name: "Climate : Pressure belts and wind systems",
        topics: [
          "Pressure belts",
          "Wind systems",
          "Planetary winds"
        ]
      },
      {
        name: "Climate : Monsoon mechanism",
        topics: [
          "Monsoon origin",
          "Indian monsoon",
          "Variability"
        ]
      },
      {
        name: "Climate : Climate classification (Köppen)",
        topics: [
          "Köppen classification",
          "Climate types",
          "Distribution"
        ]
      },
      {
        name: "Climate : Global warming and climate change",
        topics: [
          "Greenhouse effect",
          "Climate change",
          "Mitigation"
        ]
      },
      {
        name: "Resources and Development : Resource classification and sustainability",
        topics: [
          "Resource types",
          "Classification",
          "Sustainability"
        ]
      },
      {
        name: "Resources and Development : Water resource management",
        topics: [
          "Water resources",
          "Management",
          "Conservation"
        ]
      },
      {
        name: "Resources and Development : Mineral resource distribution",
        topics: [
          "Mineral distribution",
          "Mining",
          "Conservation"
        ]
      },
      {
        name: "Resources and Development : Energy resource economics",
        topics: [
          "Energy resources",
          "Economics",
          "Future trends"
        ]
      },
      {
        name: "Resources and Development : Environmental conservation",
        topics: [
          "Conservation strategies",
          "Protected areas",
          "Sustainable development"
        ]
      },
      {
        name: "Development : Economic growth vs economic development",
        topics: [
          "Growth vs development",
          "Indicators",
          "Measurement"
        ]
      },
      {
        name: "Development : Human Development Index",
        topics: [
          "HDI components",
          "Calculation",
          "India's ranking"
        ]
      },
      {
        name: "Development : Sustainable development",
        topics: [
          "Sustainability concept",
          "SDGs",
          "Implementation"
        ]
      },
      {
        name: "Development : Structural transformation of economy",
        topics: [
          "Sectoral shifts",
          "Industrialization",
          "Service economy"
        ]
      },
      {
        name: "Sectors of the Economy : Primary, secondary and tertiary sectors",
        topics: [
          "Primary sector",
          "Secondary sector",
          "Tertiary sector"
        ]
      },
      {
        name: "Sectors of the Economy : Organized vs unorganized sector",
        topics: [
          "Organized sector",
          "Unorganized sector",
          "Informal economy"
        ]
      },
      {
        name: "Sectors of the Economy : Privatization and globalization",
        topics: [
          "Privatization",
          "Globalization",
          "LPG reforms"
        ]
      },
      {
        name: "Sectors of the Economy : Public sector performance",
        topics: [
          "Public enterprises",
          "Performance",
          "Disinvestment"
        ]
      },
      {
        name: "Money & Credit : Functions of money",
        topics: [
          "Functions of money",
          "Types of money",
          "Money supply"
        ]
      },
      {
        name: "Money & Credit : Central banking system",
        topics: [
          "RBI functions",
          "Monetary authority",
          "Banking regulation"
        ]
      },
      {
        name: "Money & Credit : Monetary policy instruments",
        topics: [
          "Repo rate",
          "CRR and SLR",
          "Open market operations"
        ]
      },
      {
        name: "Money & Credit : Financial markets",
        topics: [
          "Money market",
          "Capital market",
          "Stock exchanges"
        ]
      },
      {
        name: "Democracy : Theoretical foundations of democracy",
        topics: [
          "Democratic theory",
          "Principles",
          "Types of democracy"
        ]
      },
      {
        name: "Democracy : Liberal and participatory democracy",
        topics: [
          "Liberal democracy",
          "Participatory democracy",
          "Deliberative democracy"
        ]
      },
      {
        name: "Democracy : Electoral systems",
        topics: [
          "Electoral systems",
          "FPTP vs PR",
          "Indian elections"
        ]
      },
      {
        name: "Democracy : Challenges to democracy",
        topics: [
          "Democratic challenges",
          "Populism",
          "Democratic backsliding"
        ]
      },
      {
        name: "Indian Constitution : Constituent Assembly debates",
        topics: [
          "Constituent Assembly",
          "Key debates",
          "Framers of Constitution"
        ]
      },
      {
        name: "Indian Constitution : Fundamental Rights and Duties",
        topics: [
          "Fundamental Rights",
          "Fundamental Duties",
          "Judicial interpretation"
        ]
      },
      {
        name: "Indian Constitution : Separation of powers",
        topics: [
          "Executive",
          "Legislature",
          "Judiciary"
        ]
      },
      {
        name: "Indian Constitution : Federal structure",
        topics: [
          "Federalism",
          "Centre-State relations",
          "Cooperative federalism"
        ]
      },
      {
        name: "Indian Constitution : Judicial review",
        topics: [
          "Judicial review",
          "Basic structure doctrine",
          "Judicial activism"
        ]
      }
    ]
  },
  computer_science: {
    title: "EMRS TGT Computer Science Syllabus (Official)",
    sections: [
      {
        name: "Computer Systems and Organisation : Basic Computer Organisation – Introduction to computer system, hardware, software, input device, output device, CPU, memory (primary, cache and secondary), units of memory (Bit, Byte, KB, MB, GB, TB, PB), memory hierarchy",
        topics: ["Computer system components", "Hardware and software", "CPU and memory types", "Memory units and hierarchy"]
      },
      {
        name: "Computer Systems and Organisation : Types of Software – System software (OS, utilities, drivers), programming tools, language translators (assembler, compiler, interpreter), application software",
        topics: ["System software", "Programming tools", "Language translators", "Application software"]
      },
      {
        name: "Computer Systems and Organisation : Operating System – Functions of OS, OS user interface",
        topics: ["OS functions", "User interface", "Process management"]
      },
      {
        name: "Computer Systems and Organisation : Boolean Logic – NOT, AND, OR, NAND, NOR, XOR, truth table, De Morgan's Laws, logic circuits",
        topics: ["Logic gates", "Truth tables", "De Morgan's Laws", "Logic circuits"]
      },
      {
        name: "Computer Systems and Organisation : Number System – Binary, Octal, Decimal, Hexadecimal; conversions",
        topics: ["Binary system", "Octal system", "Hexadecimal", "Number conversions"]
      },
      {
        name: "Computer Systems and Organisation : Encoding Schemes – ASCII, ISCII, UNICODE (UTF8, UTF32)",
        topics: ["ASCII encoding", "ISCII", "Unicode UTF8/UTF32"]
      },
      {
        name: "Computer Systems and Organisation : System Buses – Address, Data, Control buses; PCI",
        topics: ["Address bus", "Data bus", "Control bus", "PCI"]
      },
      {
        name: "Computer Systems and Organisation : CPU Structure – Register organization, stack organization, instruction format, addressing modes, RISC vs CISC",
        topics: ["Register organization", "Stack organization", "Addressing modes", "RISC vs CISC"]
      },
      {
        name: "Application Software : Word Processor",
        topics: ["Document creation", "Formatting", "Templates", "Mail merge"]
      },
      {
        name: "Application Software : Electronic Spreadsheet",
        topics: ["Worksheets", "Formulas", "Charts", "Data analysis"]
      },
      {
        name: "Application Software : Presentation Tools",
        topics: ["Slide creation", "Animations", "Transitions", "Multimedia"]
      },
      {
        name: "Computational Thinking and Programming : Problem Solving Steps – Algorithm, flowchart, pseudocode",
        topics: ["Algorithm design", "Flowchart symbols", "Pseudocode writing"]
      },
      {
        name: "Computational Thinking and Programming : Python Basics – Features, tokens, variables, data types",
        topics: ["Python features", "Tokens", "Variables", "Data types"]
      },
      {
        name: "Computational Thinking and Programming : Operators and Expressions",
        topics: ["Arithmetic operators", "Logical operators", "Relational operators", "Expressions"]
      },
      {
        name: "Computational Thinking and Programming : Errors – Syntax, logical, runtime",
        topics: ["Syntax errors", "Logical errors", "Runtime errors", "Debugging"]
      },
      {
        name: "Computational Thinking and Programming : Flow of Control – Conditional & iterative statements",
        topics: ["if-else statements", "Loops (for, while)", "Nested structures"]
      },
      {
        name: "Computational Thinking and Programming : Strings – Operations and built-in functions",
        topics: ["String operations", "String methods", "Slicing", "Formatting"]
      },
      {
        name: "Computational Thinking and Programming : Lists – Operations and built-in functions",
        topics: ["List operations", "List methods", "List comprehension"]
      },
      {
        name: "Computational Thinking and Programming : Tuples – Operations and built-in functions",
        topics: ["Tuple creation", "Tuple operations", "Immutability"]
      },
      {
        name: "Computational Thinking and Programming : Dictionary – Operations and built-in functions",
        topics: ["Dictionary operations", "Dictionary methods", "Key-value pairs"]
      },
      {
        name: "Computational Thinking and Programming : Python Modules – math, random, statistics",
        topics: ["math module", "random module", "statistics module"]
      },
      {
        name: "Computational Thinking and Programming : Functions – User defined, parameters, scope",
        topics: ["Function definition", "Parameters and arguments", "Scope and lifetime"]
      },
      {
        name: "Computational Thinking and Programming : File Handling – Text file, Binary file, CSV file",
        topics: ["Text file operations", "Binary file operations", "CSV file handling"]
      },
      {
        name: "Society, Law and Ethics : Digital Footprints & Net Etiquettes",
        topics: ["Digital footprint", "Online behavior", "Net etiquette"]
      },
      {
        name: "Society, Law and Ethics : Intellectual Property Rights (Copyright, Patent, Trademark)",
        topics: ["Copyright", "Patents", "Trademarks", "IPR protection"]
      },
      {
        name: "Society, Law and Ethics : Cyber Crime & Prevention",
        topics: ["Types of cyber crime", "Prevention measures", "Reporting"]
      },
      {
        name: "Society, Law and Ethics : Cyber Safety & Data Protection",
        topics: ["Data protection", "Privacy", "Safe browsing"]
      },
      {
        name: "Society, Law and Ethics : Indian IT Act",
        topics: ["IT Act 2000", "Amendments", "Provisions"]
      },
      {
        name: "Society, Law and Ethics : Technology & Society – Gender and disability issues",
        topics: ["Digital divide", "Gender issues", "Accessibility"]
      },
      {
        name: "Computer Networks : Evolution of Networking (ARPANET, NSFNET, INTERNET)",
        topics: ["ARPANET history", "NSFNET", "Internet evolution"]
      },
      {
        name: "Computer Networks : Data Communication Components & IP Address",
        topics: ["Communication components", "IP addressing", "IPv4 and IPv6"]
      },
      {
        name: "Computer Networks : Transmission Media (Wired & Wireless)",
        topics: ["Wired media", "Wireless media", "Comparison"]
      },
      {
        name: "Computer Networks : Network Devices (Modem, Switch, Router, etc.)",
        topics: ["Modem", "Switch", "Router", "Gateway"]
      },
      {
        name: "Computer Networks : Network Types (PAN, LAN, MAN, WAN) & Topologies",
        topics: ["Network types", "Topologies", "Star, Bus, Ring, Mesh"]
      },
      {
        name: "Computer Networks : Network Protocols (HTTP, FTP, SMTP, TCP/IP, etc.)",
        topics: ["HTTP/HTTPS", "FTP", "SMTP", "TCP/IP model"]
      },
      {
        name: "Computer Networks : Web Services – HTML, XML, URL, Domain, Web hosting",
        topics: ["HTML basics", "XML", "URL structure", "Web hosting"]
      },
      {
        name: "Database Management : Database Concepts & Relational Model",
        topics: ["Database concepts", "Relational model", "Tables and relations"]
      },
      {
        name: "Database Management : Keys (Primary, Foreign, Candidate, Alternate)",
        topics: ["Primary key", "Foreign key", "Candidate key", "Alternate key"]
      },
      {
        name: "Database Management : SQL – DDL, DML, Commands",
        topics: ["DDL commands", "DML commands", "SQL syntax"]
      },
      {
        name: "Database Management : Constraints & Data Types",
        topics: ["Constraints", "Data types", "NOT NULL, UNIQUE, CHECK"]
      },
      {
        name: "Database Management : SQL Functions (Math, Text, Date, Aggregate)",
        topics: ["Math functions", "Text functions", "Date functions", "Aggregate functions"]
      },
      {
        name: "Database Management : Group By, Having, Order By, Joins",
        topics: ["GROUP BY clause", "HAVING clause", "ORDER BY", "JOIN types"]
      },
      {
        name: "Emerging Trends : Artificial Intelligence, Machine Learning, NLP",
        topics: ["AI concepts", "Machine Learning", "Natural Language Processing"]
      },
      {
        name: "Emerging Trends : AR, VR, Robotics, Big Data, IoT",
        topics: ["Augmented Reality", "Virtual Reality", "Robotics", "Big Data", "IoT"]
      },
      {
        name: "Emerging Trends : Cloud Computing (SaaS, IaaS, PaaS), Grid Computing",
        topics: ["Cloud computing models", "SaaS, IaaS, PaaS", "Grid computing"]
      },
      {
        name: "Emerging Trends : Blockchain Technology",
        topics: ["Blockchain basics", "Cryptocurrency", "Applications"]
      },
      {
        name: "Artificial Intelligence : AI Domains, Ethics, Project Cycle, ML Basics, Cybersecurity in AI",
        topics: ["AI domains", "AI ethics", "ML basics", "AI in cybersecurity"]
      },
      {
        name: "Use of AI Tools : ChatGPT, Gemini, Claude, Microsoft Copilot, Canva AI",
        topics: ["ChatGPT", "Gemini", "Claude", "Microsoft Copilot", "Canva AI"]
      }
    ]
  },
  physical_education: {
    title: "EMRS TGT Physical Education Syllabus",
    sections: [
      { 
        name: "Concept of Physical Education", 
        topics: [
          "Meaning and definition of Physical Education",
          "Aim and objectives of Physical Education",
          "Modern concept and scope",
          "Need and importance",
          "Place of Physical Education in total education process"
        ] 
      },
      { 
        name: "Physiological Aspects of Physical Education", 
        topics: [
          "Effect of exercise on Muscular System",
          "Effect of exercise on Circulatory System",
          "Effect of exercise on Respiratory System",
          "Effect of exercise on Digestive System"
        ] 
      },
      { 
        name: "Psychological Aspects of Physical Education", 
        topics: [
          "Definition of Psychology",
          "Sports Psychology",
          "Achievement and Motivation in Sports",
          "Sportsmanship",
          "Sports Ethics"
        ] 
      },
      { 
        name: "Health Education and Nutrition", 
        topics: [
          "Balanced diet",
          "Components of diet",
          "Malnutrition",
          "Personal hygiene"
        ] 
      },
      { 
        name: "Organization and Administration", 
        topics: [
          "Planning of Physical Education programme",
          "Conducting tournaments and competitions",
          "Intramural and Extramural activities",
          "Record keeping"
        ] 
      },
      { 
        name: "First Aid and Injury Management", 
        topics: [
          "Principles of First Aid",
          "Treatment of sports injuries",
          "Sprain, Strain, Fracture",
          "CPR basics"
        ] 
      },
      { 
        name: "Athletics", 
        topics: [
          "Track events",
          "Field events",
          "Rules and regulations"
        ] 
      },
      { 
        name: "Major Games", 
        topics: [
          "Football – Rules and fundamentals",
          "Volleyball – Rules and fundamentals",
          "Basketball – Rules and fundamentals",
          "Kabaddi – Rules and fundamentals",
          "Kho-Kho – Rules and fundamentals"
        ] 
      },
      { 
        name: "Yoga", 
        topics: [
          "Asanas",
          "Pranayama",
          "Importance of Yoga in education"
        ] 
      },
      { 
        name: "Khelo India / Fit India", 
        topics: [
          "Objectives of Khelo India Programme",
          "Role of Fit India Movement",
          "Government initiatives for sports promotion"
        ] 
      }
    ]
  },
  music: {
    title: "EMRS TGT Music Syllabus",
    sections: [
      { 
        name: "Science of Music and Studies of Shruties", 
        topics: [
          "Vibration and frequency",
          "Pitch and its relation with vibrator",
          "Vocal and instrumental ranges of sound",
          "Amplitude and timbre",
          "Musical and unmusical overtones (Swayambhu Swar)",
          "Consonance and dissonance",
          "Main types of chords",
          "Absorption, echo, reverberation and resonance",
          "Concept of Shruti (different opinions)",
          "Placement of suddha and vikrit swaras",
          "Comparative study of Vyankatmukhi's 72 Melas",
          "Bhatkhande's Ten Thats and Modern Thirty-two Thats"
        ] 
      },
      { 
        name: "Study of Ragas and Tals", 
        topics: [
          "Critical, detailed and comparative study of Ragas",
          "Illustrations of Nyas, Alpatva, Bahutva, Avirbhava and Tirobhava",
          "Knowledge of Tals – Trital, Ektal, Rupak, Teevra, Sooltal, Jhoomra, Dhamar, Jat Tal",
          "Writing of songs in notation with Alaps and Tans",
          "Identification of Ragas from given notes"
        ] 
      },
      { 
        name: "Vocal Music", 
        topics: [
          "History of Music – Ancient to Modern period",
          "Classification of Ragas and Tals",
          "Evolution of Jatis and Ragas",
          "Revival of Indian Classical Music",
          "Comparison of Hindustani and Karnataka Music systems",
          "Impact of Modern Science on Music",
          "Study of Musical Styles – Dhrupad, Dhamar, Khayal, Thumri, Tappa, Chaturang, Tarana, Trivat",
          "Writing notation with Alaps, Tans, Boltans",
          "Biographies – Bhatkhande, Vishnudigamber, Tansen, Ameer Khusroo, Faiyyaz Khan, Pt. Ravi Shankar"
        ] 
      },
      { 
        name: "Instrumental Music", 
        topics: [
          "Notation systems – Bhatkhande, Vishnudigamber and Western",
          "Western notes and intervals",
          "Musical scales – Diatonic, Major, Minor",
          "Harmony and melody",
          "Placement of notes on Veena (Pt. Srinivas)",
          "Comparative study of Northern and Southern Tal paddhatis",
          "Study of Gharanas and Baithaks",
          "Writing Gats in notation with Alaps, Todas, Jhalas",
          "Identification of Ragas from given notes"
        ] 
      },
      { 
        name: "Percussion", 
        topics: [
          "Detailed study of Tals – Teental, Jhaptal, Rupak, Choutal, Sooltal, Teevra, Tilwada, Dadra, Kaharwa",
          "Layakaries – Dugun, Tigun, Chougun, Ada",
          "Tukras, Parans, Tihais, Kayadas, Paltas, Relas, Peshkaras, Mukhras",
          "Recognition of Tals from given Bols",
          "Writing in notation"
        ] 
      }
    ]
  },
  art: {
    title: "EMRS TGT Art Syllabus",
    sections: [
      { 
        name: "Art of Indus Valley", 
        topics: [
          "Introduction – Period and Location",
          "Extension – Harappa, Mohenjo-Daro, Ropar, Lothal, Rangpur, Alamgirpur, Kalibangan, Banawali, Dholavira",
          "Sculptures – Dancing Girl (Bronze), Male Torso (Stone), Mother Goddess (Terracotta)",
          "Seals – Bull (Stone)",
          "Painted Earthen Ware (Jar Decoration)"
        ] 
      },
      { 
        name: "Buddhist, Jain and Hindu Art", 
        topics: [
          "Mauryan, Shunga, Kushana and Gupta Period – General Introduction",
          "Lion Capital (Sarnath)",
          "Chauri Bearer (Didarganj)",
          "Bodhisattva Head (Taxila – Gandhara Period)",
          "Seated Buddha (Mathura & Sarnath)",
          "Jain Tirthankara (Gupta Period)",
          "Ajanta – Location, Period, Caves, Chaitya & Vihara",
          "Padmapani Bodhisattva (Ajanta Cave I)",
          "Mara Vijay (Ajanta Cave 26)"
        ] 
      },
      { 
        name: "Temple Sculpture", 
        topics: [
          "Introduction to Indian Temple Architecture",
          "Descent of Ganga (Mahabalipuram – Pallava Period)",
          "Ravana Shaking Mount Kailash (Ellora – Rashtrakuta Period)",
          "Trimurti (Elephanta)",
          "Nataraja (Chola Bronze)"
        ] 
      },
      { 
        name: "Indo-Islamic Architecture", 
        topics: [
          "Qutub Minar",
          "Taj Mahal",
          "Gol Gumbaz"
        ] 
      },
      { 
        name: "Indian Miniature Painting", 
        topics: [
          "Western Indian & Pala Schools",
          "Rajasthani Schools – Mewar, Bundi, Jodhpur, Bikaner, Kishangarh, Jaipur",
          "Study of Maru Ragini, Raja Aniruddha Singh, Chaugan Players, Krishna on Swing, Radha (Bani Thani), Bharat Meets Rama",
          "Pahari Schools – Basohli & Kangra",
          "Krishna with Gopis, Raga Megha"
        ] 
      },
      { 
        name: "Mughal School", 
        topics: [
          "Origin and Development",
          "Main Features",
          "Krishna Lifting Govardhan, Babur Crossing River Sone",
          "Jahangir Holding Madonna, Falcon on Bird Rest"
        ] 
      },
      { 
        name: "Deccan School", 
        topics: [
          "Origin and Features",
          "Raga Hindola, Chand Bibi Playing Polo"
        ] 
      },
      { 
        name: "Bengal School", 
        topics: [
          "Origin and Development",
          "Rama Vanquishing the Pride of the Ocean – Raja Ravi Verma",
          "Journey's End – Rabindranath Tagore",
          "Parthasarathi – Nandlal Bose",
          "Radhika – M.A.R. Chughtai"
        ] 
      },
      { 
        name: "Modern Trends in Indian Art", 
        topics: [
          "Magician – Gaganendranath Tagore",
          "Mother and Child – Jamini Roy",
          "Tree Girls – Amrita Sher-Gil",
          "Contemporary Artists – M.F. Hussain, K.K. Hebbar, Tyeb Mehta"
        ] 
      },
      { 
        name: "Modern Sculpture", 
        topics: [
          "D.P. Roychowdhury, Ramkinker Baij, Amar Nath Sehgal"
        ] 
      }
    ]
  }
};

// Sample Descriptive Questions for Each Subject (Class 12 Level - 4 Marks Each)
const descriptiveQuestions = {
  // PGT Mathematics
  mathematics: [
    { id: 1, question: "Prove that the function f(x) = |x| is continuous but not differentiable at x = 0. Illustrate with graph.", topic: "Continuity and Differentiability" },
    { id: 2, question: "Using integration, find the area of the region bounded by y² = 4x and x² = 4y.", topic: "Applications of Integration" },
    { id: 3, question: "Solve the differential equation dy/dx + y·cot x = 2x + x²·cot x, given that y = 0 when x = π/2.", topic: "Differential Equations" },
    { id: 4, question: "Find the inverse of matrix A = [[1,2,3],[0,2,4],[0,0,5]] using elementary row operations.", topic: "Matrices" },
    { id: 5, question: "If y = (tan⁻¹x)², show that (x² + 1)²y₂ + 2x(x² + 1)y₁ = 2.", topic: "Differentiation" },
    { id: 6, question: "Evaluate the definite integral ∫₀^π x·sin x/(1 + cos²x) dx using properties of definite integrals.", topic: "Definite Integrals" },
    { id: 7, question: "Find the equation of the plane passing through points (1,1,0), (1,2,1) and (-2,2,-1). Also find distance from origin.", topic: "Three Dimensional Geometry" },
    { id: 8, question: "If A and B are two events such that P(A) = 1/4, P(B) = 1/2 and P(A∩B) = 1/8, find P(not A and not B).", topic: "Probability" },
    { id: 9, question: "Prove that the relation R on set A = {1,2,3,4,5} given by R = {(a,b): |a-b| is even} is an equivalence relation.", topic: "Relations and Functions" },
    { id: 10, question: "Find the maximum and minimum values of f(x) = sin x + cos x in [0, π/2]. Also find at what points these occur.", topic: "Applications of Derivatives" }
  ],
  // PGT Physics
  physics: [
    { id: 1, question: "Derive an expression for the electric field at a point on the axial line of an electric dipole. What happens when point is far from dipole?", topic: "Electrostatics" },
    { id: 2, question: "State and prove Gauss's law in electrostatics. Use it to find electric field due to infinitely long straight uniformly charged wire.", topic: "Gauss's Law" },
    { id: 3, question: "Explain the working principle of a cyclotron with diagram. Derive expression for cyclotron frequency.", topic: "Moving Charges and Magnetism" },
    { id: 4, question: "Derive an expression for the magnetic field at a point on the axis of a circular current carrying coil using Biot-Savart law.", topic: "Magnetic Effects of Current" },
    { id: 5, question: "Explain the phenomenon of electromagnetic induction. State and explain Lenz's law with suitable example.", topic: "Electromagnetic Induction" },
    { id: 6, question: "Derive lens maker's formula for a thin lens. Under what conditions does a convex lens behave as diverging lens?", topic: "Ray Optics" },
    { id: 7, question: "Explain Huygens' principle. Use it to verify laws of refraction of light (Snell's law).", topic: "Wave Optics" },
    { id: 8, question: "Explain Einstein's photoelectric equation. How does it explain the experimental observations of photoelectric effect?", topic: "Dual Nature of Radiation" },
    { id: 9, question: "Derive expression for radius of nth Bohr orbit and energy of electron in that orbit for hydrogen atom.", topic: "Atoms" },
    { id: 10, question: "Explain the principle and working of nuclear reactor. What is the role of moderator and control rods?", topic: "Nuclear Physics" }
  ],
  // PGT Chemistry
  chemistry: [
    { id: 1, question: "Derive integrated rate equation for first order reaction. How do you determine order from half-life values?", topic: "Chemical Kinetics" },
    { id: 2, question: "Explain the crystal field theory for octahedral complexes. Why is [CoF₆]³⁻ paramagnetic while [Co(NH₃)₆]³⁺ is diamagnetic?", topic: "Coordination Compounds" },
    { id: 3, question: "What is electrochemical series? Explain how it helps in predicting the feasibility of redox reactions.", topic: "Electrochemistry" },
    { id: 4, question: "Explain SN1 and SN2 mechanisms with energy profile diagrams. What factors favor each mechanism?", topic: "Organic Reactions" },
    { id: 5, question: "Write the mechanism of aldol condensation reaction. How does cross aldol condensation differ from simple aldol?", topic: "Aldehydes and Ketones" },
    { id: 6, question: "Explain the hybridization in SF₆, IF₇ and XeF₄ molecules. Draw their structures showing bond angles.", topic: "Chemical Bonding" },
    { id: 7, question: "Derive Nernst equation for electrode potential. Calculate EMF of Daniel cell at 298K when [Zn²⁺]=0.1M and [Cu²⁺]=0.01M.", topic: "Electrochemistry" },
    { id: 8, question: "Explain colligative properties. Derive expression for elevation in boiling point of solution.", topic: "Solutions" },
    { id: 9, question: "What are lanthanide contraction? Explain its causes and consequences.", topic: "f-Block Elements" },
    { id: 10, question: "Explain the mechanism of enzyme catalysis. What is the effect of pH and temperature on enzyme activity?", topic: "Surface Chemistry" }
  ],
  // PGT Biology
  biology: [
    { id: 1, question: "Describe the mechanism of DNA replication. Explain why it is called semi-conservative.", topic: "Molecular Basis of Inheritance" },
    { id: 2, question: "Explain the light reactions of photosynthesis. Where do they occur and what products are formed?", topic: "Photosynthesis" },
    { id: 3, question: "Describe the structure of nephron with diagram. Explain the mechanism of urine formation.", topic: "Excretory System" },
    { id: 4, question: "What is central dogma of molecular biology? Explain the process of transcription in prokaryotes.", topic: "Gene Expression" },
    { id: 5, question: "Explain Hardy-Weinberg principle. What are the factors affecting Hardy-Weinberg equilibrium?", topic: "Evolution" },
    { id: 6, question: "Describe the human respiratory system. Explain the mechanism of breathing with the role of respiratory muscles.", topic: "Breathing and Exchange of Gases" },
    { id: 7, question: "What is an ecological pyramid? Explain different types of ecological pyramids with examples.", topic: "Ecosystem" },
    { id: 8, question: "Explain the working of human heart with diagram. Describe double circulation in humans.", topic: "Body Fluids and Circulation" },
    { id: 9, question: "Describe the menstrual cycle in human females. Explain the hormonal regulation of menstrual cycle.", topic: "Human Reproduction" },
    { id: 10, question: "Explain the mechanism of muscle contraction according to sliding filament theory.", topic: "Locomotion and Movement" }
  ],
  // TGT Hindi
  hindi: [
    { id: 1, question: "कबीर की साखियों में व्यक्त सामाजिक चेतना का वर्णन कीजिए। उदाहरण सहित समझाइए।", topic: "भक्तिकालीन काव्य" },
    { id: 2, question: "'गोदान' उपन्यास के आधार पर ग्रामीण जीवन की समस्याओं का विश्लेषण कीजिए।", topic: "उपन्यास साहित्य" },
    { id: 3, question: "रस की परिभाषा देते हुए वीर रस का स्थायी भाव, विभाव, अनुभाव और संचारी भाव लिखिए।", topic: "काव्यशास्त्र" },
    { id: 4, question: "छायावाद की प्रमुख विशेषताओं का वर्णन कीजिए। किन्हीं दो छायावादी कवियों का परिचय दीजिए।", topic: "आधुनिक काव्य" },
    { id: 5, question: "'भाषा शिक्षण' में व्याकरण की भूमिका स्पष्ट कीजिए। व्याकरण शिक्षण की विधियाँ बताइए।", topic: "भाषा शिक्षण" },
    { id: 6, question: "समास की परिभाषा देते हुए द्वंद्व और द्विगु समास में अंतर उदाहरण सहित स्पष्ट कीजिए।", topic: "व्याकरण" },
    { id: 7, question: "'पर्यावरण प्रदूषण' विषय पर एक निबंध लिखिए जिसमें भूमिका, विषय-विस्तार और उपसंहार हो।", topic: "निबंध लेखन" },
    { id: 8, question: "तुलसीदास की 'रामचरितमानस' में वर्णित राम के आदर्श चरित्र का विवेचन कीजिए।", topic: "मध्यकालीन काव्य" },
    { id: 9, question: "अलंकार किसे कहते हैं? उपमा और रूपक अलंकार में अंतर उदाहरण सहित समझाइए।", topic: "अलंकार" },
    { id: 10, question: "हिंदी गद्य के विकास में भारतेंदु युग के योगदान का वर्णन कीजिए।", topic: "हिंदी साहित्य का इतिहास" }
  ],
  // TGT English
  english: [
    { id: 1, question: "Discuss the theme of 'The Road Not Taken' by Robert Frost. How does the poet convey the idea of choices in life?", topic: "Poetry Analysis" },
    { id: 2, question: "Write a letter to the editor of a newspaper expressing your concern about increasing pollution in your city.", topic: "Letter Writing" },
    { id: 3, question: "Explain the use of direct and indirect speech. Transform the following into indirect speech with rules.", topic: "Grammar" },
    { id: 4, question: "Discuss the role of technology in modern education. Write an article for your school magazine (150-200 words).", topic: "Article Writing" },
    { id: 5, question: "What is a metaphor? How is it different from simile? Give three examples of each from literature.", topic: "Figures of Speech" },
    { id: 6, question: "Explain the character sketch of Portia from 'The Merchant of Venice'. What qualities make her an ideal character?", topic: "Drama" },
    { id: 7, question: "Write a speech on 'Importance of Physical Fitness' to be delivered in your school morning assembly.", topic: "Speech Writing" },
    { id: 8, question: "Discuss the various methods of teaching English as a second language. Which method do you find most effective and why?", topic: "Language Teaching" },
    { id: 9, question: "What is reading comprehension? Explain different strategies to improve reading skills in students.", topic: "Reading Skills" },
    { id: 10, question: "Write a paragraph on 'Digital India' highlighting its benefits and challenges (100-120 words).", topic: "Paragraph Writing" }
  ],
  // TGT Science
  science: [
    { id: 1, question: "Explain the process of photosynthesis with a neat labelled diagram. Write the balanced chemical equation.", topic: "Life Processes" },
    { id: 2, question: "State Ohm's Law. Derive the expression for equivalent resistance when resistors are connected in series and parallel.", topic: "Electricity" },
    { id: 3, question: "What is refraction of light? State Snell's law and explain total internal reflection with applications.", topic: "Light" },
    { id: 4, question: "Describe the structure of human heart with diagram. Explain double circulation in humans.", topic: "Life Processes" },
    { id: 5, question: "Explain the modern periodic table. How do atomic size and electronegativity vary along a period and group?", topic: "Periodic Classification" },
    { id: 6, question: "What is electromagnetic induction? State Faraday's laws. Explain working of AC generator with diagram.", topic: "Magnetic Effects" },
    { id: 7, question: "Describe the human excretory system. Explain the structure and function of nephron.", topic: "Life Processes" },
    { id: 8, question: "What are acids, bases and salts? Explain pH scale. How does pH affect our daily life?", topic: "Chemical Reactions" },
    { id: 9, question: "Explain the carbon cycle in nature. Why is carbon cycle important for maintaining life on Earth?", topic: "Environment" },
    { id: 10, question: "Derive the three equations of motion. A car starts from rest and attains a velocity of 72 km/h in 10 seconds. Calculate acceleration and distance.", topic: "Motion" }
  ],
  // TGT Social Science
  social_science: [
    { id: 1, question: "Explain the main features of Indian Constitution. What is the significance of Preamble?", topic: "Constitution" },
    { id: 2, question: "Describe the causes and consequences of French Revolution. How did it impact the world?", topic: "History" },
    { id: 3, question: "What are the main physical divisions of India? Describe the Northern Plains with diagram.", topic: "Geography" },
    { id: 4, question: "Explain the role of Mahatma Gandhi in Indian Freedom Movement. Describe the significance of Salt March.", topic: "Freedom Struggle" },
    { id: 5, question: "What is democracy? Explain features of Indian democratic system. Why is democracy better than other forms?", topic: "Civics" },
    { id: 6, question: "Describe the monsoon mechanism in India. Why is monsoon important for Indian agriculture?", topic: "Climate" },
    { id: 7, question: "What were the causes and effects of Industrial Revolution? How did it change society?", topic: "History" },
    { id: 8, question: "Explain the Fundamental Rights guaranteed by Indian Constitution. Why are they called fundamental?", topic: "Civics" },
    { id: 9, question: "Describe the major rivers of India. Explain the importance of rivers for Indian economy.", topic: "Geography" },
    { id: 10, question: "Explain the causes and impact of Revolt of 1857. Why is it called First War of Independence?", topic: "History" }
  ],
  // Commerce
  commerce: [
    { id: 1, question: "Explain double-entry system of bookkeeping. State the golden rules of accounting with examples.", topic: "Accounting Principles" },
    { id: 2, question: "What is depreciation? Explain straight-line and written down value methods with numerical example.", topic: "Depreciation" },
    { id: 3, question: "Differentiate between capital and revenue expenditure. Give three examples of each.", topic: "Financial Statements" },
    { id: 4, question: "Explain the meaning and features of Joint Stock Company. How is it different from partnership?", topic: "Business Organization" },
    { id: 5, question: "What is bank reconciliation statement? Why is it prepared? Explain with suitable example.", topic: "Bank Reconciliation" },
    { id: 6, question: "Define ratio analysis. Calculate current ratio and quick ratio from given data.", topic: "Ratio Analysis" },
    { id: 7, question: "Explain the concept of working capital. What factors determine working capital requirements?", topic: "Financial Management" },
    { id: 8, question: "What is goodwill? Explain super profit method of valuing goodwill with example.", topic: "Goodwill" },
    { id: 9, question: "Describe the methods of capital budgeting. Explain payback period method with example.", topic: "Investment Decisions" },
    { id: 10, question: "What are bills of exchange? Differentiate between bills receivable and bills payable with journal entries.", topic: "Bills of Exchange" }
  ],
  // Economics
  economics: [
    { id: 1, question: "State and explain the law of demand with diagram. What are the exceptions to law of demand?", topic: "Demand Theory" },
    { id: 2, question: "Define GDP. Explain income method and expenditure method of calculating National Income.", topic: "National Income" },
    { id: 3, question: "What is credit creation by commercial banks? Explain with numerical example. What are its limitations?", topic: "Money and Banking" },
    { id: 4, question: "Explain price elasticity of demand. Calculate elasticity when price changes from ₹20 to ₹16 and quantity from 100 to 150.", topic: "Elasticity" },
    { id: 5, question: "What is fiscal policy? Explain how government uses taxation and expenditure to control inflation.", topic: "Fiscal Policy" },
    { id: 6, question: "Explain the law of supply with diagram. What are the determinants of supply?", topic: "Supply Theory" },
    { id: 7, question: "Describe consumer equilibrium under utility analysis. State law of diminishing marginal utility.", topic: "Consumer Behavior" },
    { id: 8, question: "What is market equilibrium? Explain with diagram how equilibrium price and quantity are determined.", topic: "Market Equilibrium" },
    { id: 9, question: "Explain monetary policy tools of RBI. How do repo rate and CRR affect money supply?", topic: "Monetary Policy" },
    { id: 10, question: "What is balance of payments? Differentiate between current account and capital account.", topic: "Balance of Payments" }
  ],
  // Geography
  geography: [
    { id: 1, question: "Explain plate tectonic theory. How does movement of plates cause earthquakes and volcanic eruptions?", topic: "Interior of Earth" },
    { id: 2, question: "Describe the monsoon mechanism in India. What factors affect the distribution of rainfall in India?", topic: "Climate" },
    { id: 3, question: "Explain the rock cycle with diagram. Differentiate between igneous, sedimentary and metamorphic rocks.", topic: "Rocks and Minerals" },
    { id: 4, question: "Describe the distribution of iron ore in India. Why is iron ore important for industrial development?", topic: "Mineral Resources" },
    { id: 5, question: "What is population density? Explain the factors affecting distribution of population in India.", topic: "Population Geography" },
    { id: 6, question: "Describe the physiographic divisions of India. Explain the characteristics of Himalayan mountain system.", topic: "Physical Features" },
    { id: 7, question: "What is soil erosion? Explain different types and methods of soil conservation in India.", topic: "Soil Resources" },
    { id: 8, question: "Describe the drainage system of India. Explain the importance of rivers for Indian agriculture.", topic: "Drainage" },
    { id: 9, question: "Explain different types of natural vegetation in India. What factors affect vegetation distribution?", topic: "Natural Vegetation" },
    { id: 10, question: "What is sustainable development? Explain importance of resource conservation with examples.", topic: "Development" }
  ],
  // History
  history: [
    { id: 1, question: "Describe the salient features of Indus Valley Civilization. What were the possible causes of its decline?", topic: "Ancient India" },
    { id: 2, question: "Explain the causes and significance of Non-Cooperation Movement. Why was it withdrawn?", topic: "National Movement" },
    { id: 3, question: "Describe the administrative system of Mauryan Empire. What was Ashoka's Dhamma?", topic: "Ancient India" },
    { id: 4, question: "Explain the main causes of Industrial Revolution in England. What were its social and economic impacts?", topic: "World History" },
    { id: 5, question: "Describe the making of Indian Constitution. What are the main features of Preamble?", topic: "Modern India" },
    { id: 6, question: "What was Subsidiary Alliance? How did it help British expansion in India?", topic: "British Rule" },
    { id: 7, question: "Explain the causes and consequences of Revolt of 1857. Why is it called First War of Independence?", topic: "British Rule" },
    { id: 8, question: "Describe the Mansabdari system of Akbar. How did it strengthen Mughal administration?", topic: "Medieval India" },
    { id: 9, question: "Compare the ideologies of Moderates and Extremists in Indian National Congress.", topic: "National Movement" },
    { id: 10, question: "What were the causes of World War I? Explain its impact on world economy and society.", topic: "World History" }
  ],
  // Computer Science (PGT)
  computer_science: [
    { id: 1, question: "Explain the concept of Object-Oriented Programming. What are the four pillars of OOP? Give examples.", topic: "OOP Concepts" },
    { id: 2, question: "What is a database? Explain normalization and its different normal forms with examples.", topic: "Database Management" },
    { id: 3, question: "Explain different types of operating systems. What is the role of process scheduling in OS?", topic: "Operating Systems" },
    { id: 4, question: "What is a data structure? Explain the difference between stack and queue with applications.", topic: "Data Structures" },
    { id: 5, question: "Explain the TCP/IP model. Compare it with OSI model.", topic: "Computer Networks" },
    { id: 6, question: "What is an algorithm? Explain time complexity and space complexity with examples.", topic: "Algorithms" },
    { id: 7, question: "Describe different types of cyber threats. What security measures can protect against them?", topic: "Cyber Security" },
    { id: 8, question: "Explain the concept of recursion in programming. Write recursive algorithm for factorial calculation.", topic: "Programming" },
    { id: 9, question: "What is SQL? Explain DDL and DML commands with examples.", topic: "Database" },
    { id: 10, question: "Explain cloud computing and its service models (IaaS, PaaS, SaaS) with examples.", topic: "Cloud Computing" }
  ],
  // Home Science
  home_science: [
    { id: 1, question: "Explain the balanced diet concept. What are the functions of proteins, carbohydrates and fats in our body?", topic: "Nutrition" },
    { id: 2, question: "Describe the stages of child development from birth to 5 years. What factors affect child development?", topic: "Child Development" },
    { id: 3, question: "What is food preservation? Explain different methods of food preservation with examples.", topic: "Food Science" },
    { id: 4, question: "Explain the principles of interior decoration. What factors should be considered while furnishing a home?", topic: "Home Management" },
    { id: 5, question: "Describe different types of textiles. How do you identify natural and synthetic fibres?", topic: "Textiles" },
    { id: 6, question: "What is family budgeting? Explain the steps in preparing a family budget.", topic: "Resource Management" },
    { id: 7, question: "Explain the importance of hygiene and sanitation. What are the causes and prevention of food-borne diseases?", topic: "Health and Hygiene" },
    { id: 8, question: "Describe the nutritional requirements during pregnancy and lactation. What dietary modifications are needed?", topic: "Nutrition" },
    { id: 9, question: "What is consumer protection? Explain the rights of consumers under Consumer Protection Act.", topic: "Consumer Education" },
    { id: 10, question: "Explain the principles of meal planning. How do you plan meals for a family considering nutrition and budget?", topic: "Food Management" }
  ],
  // Music
  music: [
    { id: 1, question: "Explain the concept of Raga in Indian Classical Music. What are the characteristics of Raga Yaman?", topic: "Indian Classical Music" },
    { id: 2, question: "Describe the structure of a Tala. Explain Teentaal with its divisions and matras.", topic: "Tala System" },
    { id: 3, question: "What is the importance of Shruti in Indian music? How many Shrutis are there and what is their significance?", topic: "Music Theory" },
    { id: 4, question: "Explain the contribution of Tansen to Indian music. Describe any two of his compositions.", topic: "Music History" },
    { id: 5, question: "What are the different types of musical instruments? Classify Indian instruments with examples.", topic: "Musical Instruments" },
    { id: 6, question: "Describe the Thaat system in Hindustani Classical Music. Name the ten Thaats with one raga each.", topic: "Raga Classification" },
    { id: 7, question: "Explain the concept of Bandish. What are its different types in Hindustani vocal music?", topic: "Vocal Music" },
    { id: 8, question: "What is Gharana in Indian Classical Music? Describe any two famous Gharanas of vocal music.", topic: "Music Tradition" },
    { id: 9, question: "Explain the role of music education in schools. What methods can make music learning effective?", topic: "Music Education" },
    { id: 10, question: "Compare and contrast Hindustani and Carnatic music systems.", topic: "Comparative Music" }
  ],
  // Physical Education
  physical_education: [
    { id: 1, question: "Explain the importance of physical education in school curriculum. What are its objectives?", topic: "Physical Education" },
    { id: 2, question: "Describe the components of physical fitness. How can we develop muscular strength and endurance?", topic: "Physical Fitness" },
    { id: 3, question: "What are the rules and regulations of Volleyball? Explain different positions of players.", topic: "Games" },
    { id: 4, question: "Explain the concept of sports injuries. Describe first aid treatment for sprains and fractures.", topic: "Sports Medicine" },
    { id: 5, question: "What is yoga? Explain the benefits of Surya Namaskar with steps.", topic: "Yoga" },
    { id: 6, question: "Describe the track and field events in athletics. What are the rules for relay race?", topic: "Athletics" },
    { id: 7, question: "Explain the importance of warm-up and cool-down exercises. Give examples of each.", topic: "Exercise Physiology" },
    { id: 8, question: "What is sports psychology? How does motivation affect sports performance?", topic: "Sports Psychology" },
    { id: 9, question: "Describe the nutritional requirements of sports persons. What role does diet play in sports?", topic: "Sports Nutrition" },
    { id: 10, question: "Explain the tournament systems in sports. Draw fixtures for knockout tournament of 16 teams.", topic: "Sports Management" }
  ],
  // Art
  art: [
    { id: 1, question: "Describe the characteristics of Mughal miniature paintings. Name two famous painters of Mughal court.", topic: "Indian Art History" },
    { id: 2, question: "What are the elements and principles of art? Explain with examples from visual arts.", topic: "Art Fundamentals" },
    { id: 3, question: "Explain the concept of perspective in drawing. What are the types of perspective?", topic: "Drawing Techniques" },
    { id: 4, question: "Describe the features of Ajanta cave paintings. Why are they considered masterpieces?", topic: "Ancient Indian Art" },
    { id: 5, question: "What is color theory? Explain primary, secondary and complementary colors with color wheel.", topic: "Color Theory" },
    { id: 6, question: "Explain the importance of art education in schools. How does art develop creativity in students?", topic: "Art Education" },
    { id: 7, question: "Describe the Bengal School of Art. What was its contribution to Indian art renaissance?", topic: "Modern Indian Art" },
    { id: 8, question: "What are different types of sculptures? Explain the process of relief sculpture.", topic: "Sculpture" },
    { id: 9, question: "Describe the folk art traditions of India. Explain any two folk art forms with their characteristics.", topic: "Folk Art" },
    { id: 10, question: "What is graphic design? Explain its importance in modern visual communication.", topic: "Applied Art" }
  ]
};

const SyllabusSection = ({ section }) => {
  const [isOpen, setIsOpen] = useState(true);
  
  return (
    <div className="border border-gray-200 rounded-lg mb-4 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-colors"
      >
        <span className="font-semibold text-gray-800">{section.name}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
      </button>
      {isOpen && (
        <ul className="p-4 space-y-2 bg-white">
          {section.topics.map((topic, idx) => (
            <li key={idx} className="flex items-start gap-2 text-gray-700">
              <span className="text-blue-500 mt-1">•</span>
              <span>{topic}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Generate a unique device ID for this browser
const getDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = 'web_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
    localStorage.setItem('device_id', deviceId);
  }
  return deviceId;
};

// Auth Modal Component for Syllabus Page
const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [isLogin, setIsLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", mobile: "", password: "" });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotMobile, setForgotMobile] = useState("");
  const [retrievedPassword, setRetrievedPassword] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      // Login: accept mobile or email
      const input = formData.mobile.trim();
      const isEmail = input.includes('@');
      if (!isEmail && !/^\d{10}$/.test(input.replace(/[\s\-]/g, ''))) {
        toast.error("Enter a valid 10-digit mobile number or email");
        return;
      }
      
      setLoading(true);
      try {
        const response = await axios.post(`${API}/auth/login`, {
          mobile: input,
          device_id: getDeviceId(),
          device_type: 'web'
        });
        localStorage.setItem('session_token', response.data.session_token);
        toast.success("Login successful!");
        onSuccess(response.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Login failed");
      } finally {
        setLoading(false);
      }
    } else {
      // Registration flow
      const mobileRegex = /^\d{10}$/;
      if (!mobileRegex.test(formData.mobile.replace(/[\s\-]/g, ''))) {
        toast.error("Please enter a valid 10-digit mobile number");
        return;
      }
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email address");
        return;
      }
      
      setLoading(true);
      try {
        await axios.post(`${API}/auth/register`, formData);
        // Auto login after registration
        const loginResponse = await axios.post(`${API}/auth/login`, {
          mobile: formData.mobile,
          device_id: getDeviceId(),
          device_type: 'web'
        });
        localStorage.setItem('session_token', loginResponse.data.session_token);
        toast.success("Registration successful!");
        onSuccess(loginResponse.data);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Registration failed");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(forgotMobile.replace(/[\s\-]/g, ''))) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { mobile: forgotMobile });
      setRetrievedPassword(response.data.password);
      toast.success("Password retrieved successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Mobile number not found");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Forgot Password View
  if (showForgotPassword) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative animate-in fade-in zoom-in duration-200">
          <button 
            onClick={() => {
              setShowForgotPassword(false);
              setRetrievedPassword(null);
              setForgotMobile("");
            }}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="p-6">
            <div className="text-center mb-6">
              <Lock className="w-12 h-12 text-blue-600 mx-auto mb-2" />
              <h2 className="text-2xl font-bold text-gray-800">Forgot Password?</h2>
              <p className="text-gray-500 text-sm mt-1">
                Enter your registered mobile number
              </p>
            </div>

            {!retrievedPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-mobile-modal">Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="forgot-mobile-modal"
                      type="tel"
                      placeholder="9876543210"
                      className="pl-10"
                      value={forgotMobile}
                      onChange={(e) => setForgotMobile(e.target.value)}
                      pattern="[0-9]{10}"
                      maxLength="10"
                      required
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                  disabled={loading}
                >
                  {loading ? "Retrieving..." : "Retrieve Password"}
                </Button>
              </form>
            ) : (
              <div className="text-center space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-gray-600 mb-2">Your password is:</p>
                  <p className="text-2xl font-bold text-green-700">{retrievedPassword}</p>
                </div>
                <Button 
                  onClick={() => {
                    setShowForgotPassword(false);
                    setRetrievedPassword(null);
                    setForgotMobile("");
                  }}
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative animate-in fade-in zoom-in duration-200">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="p-6">
          <div className="text-center mb-6">
            <GraduationCap className="w-12 h-12 text-blue-600 mx-auto mb-2" />
            <h2 className="text-2xl font-bold text-gray-800">
              {isLogin ? "Welcome Back!" : "Start Practicing Now"}
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {isLogin ? "Login to continue" : "Create an account to get started"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your name"
                      className="pl-10"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={!isLogin}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      className="pl-10"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required={!isLogin}
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="mobile">{isLogin ? "Mobile Number or Email *" : "Mobile Number *"}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="mobile"
                  type={isLogin ? "text" : "tel"}
                  placeholder={isLogin ? "9876543210 or you@email.com" : "9876543210"}
                  className="pl-10"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: isLogin ? e.target.value : e.target.value.replace(/\D/g, '') })}
                  maxLength={isLogin ? undefined : 10}
                  required
                />
              </div>
              {isLogin && <p className="text-xs text-gray-500">No password needed — just your registered mobile or email</p>}
              {!isLogin && <p className="text-xs text-gray-500">Used for login</p>}
            </div>
            
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a password"
                    className="pl-10"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            {isLogin && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-xs text-blue-700">
                <strong>1 device at a time:</strong> Your account can only be active on 1 device/browser.
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              disabled={loading}
            >
              {loading ? "Please wait..." : (isLogin ? "Login & Practice" : "Register & Practice")}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button 
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-blue-600 hover:underline"
            >
              {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SyllabusPage() {
  const { type, subject } = useParams();
  const navigate = useNavigate();
  const isPGT = type === 'pgt';
  const isNonTeaching = type === 'non_teaching';
  
  // Get correct syllabus data based on exam type
  const syllabusData = isNonTeaching 
    ? nonTeachingSyllabusData 
    : (isPGT ? pgtSyllabusData : tgtSyllabusData);
  
  const data = syllabusData[subject] || (isNonTeaching ? nonTeachingSyllabusData.hostel_warden : (isPGT ? pgtSyllabusData.mathematics : tgtSyllabusData.hindi));
  
  const allSubjects = Object.keys(syllabusData);
  
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Dynamic SEO data
  const subjectName = (subject || 'hindi').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const examType = type === 'non_teaching' ? 'Non-Teaching' : (type || 'tgt').toUpperCase();
  const topicCount = data?.sections?.length || 0;
  const topicNames = (data?.sections || []).slice(0, 5).map(s => s.name.split(' : ')[0]).join(', ');
  
  const seoTitle = `EMRS ${examType} ${subjectName} Syllabus 2026 | KVS NVS Tier-2 Complete Syllabus & Practice`;
  const seoDesc = `Complete EMRS ${examType} ${subjectName} syllabus for Tier-2 exam 2026. ${topicCount} topics covering ${topicNames}. Practice descriptive test questions with AI evaluation. KVS NVS ${examType} ${subjectName} exam pattern, study notes, mock tests.`;
  const seoKeywords = `EMRS ${examType} ${subjectName} syllabus, KVS ${examType} ${subjectName} syllabus, NVS ${examType} ${subjectName}, EMRS ${examType} ${subjectName} syllabus 2026, KVS ${examType} ${subjectName} exam pattern, ${examType} ${subjectName} tier 2, EMRS ${subjectName} descriptive test, KVS ${subjectName} descriptive paper, NVS ${subjectName} practice, ${examType} ${subjectName} NCERT notes, ${examType} ${subjectName} mock test, ${examType} ${subjectName} previous year papers, EMRS ${examType} recruitment 2026, KVS ${examType} preparation, ${subjectName} teacher exam syllabus`;
  const canonicalUrl = `https://descriptivetest.com/syllabus/${type}/${subject}`;
  
  const syllabusSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `EMRS ${examType} ${subjectName} - Complete Syllabus & Practice`,
    "description": seoDesc,
    "url": canonicalUrl,
    "provider": { "@type": "Organization", "name": "Descriptive Test", "url": "https://descriptivetest.com" },
    "educationalLevel": `${examType} Level`,
    "about": { "@type": "Thing", "name": subjectName },
    "inLanguage": ["en", "hi"],
    "numberOfCredits": topicCount,
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "name": `${examType} ${subjectName} Tier-2 Practice`,
      "description": `Online practice for EMRS KVS NVS ${examType} ${subjectName} Tier-2 exam`
    }
  };
  
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://descriptivetest.com/" },
      { "@type": "ListItem", "position": 2, "name": `${examType} Subjects`, "item": `https://descriptivetest.com/category/${type}` },
      { "@type": "ListItem", "position": 3, "name": `${subjectName} Syllabus`, "item": canonicalUrl }
    ]
  };
  
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `What is the syllabus for EMRS ${examType} ${subjectName} 2026?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `The EMRS ${examType} ${subjectName} syllabus for 2026 covers ${topicCount} topics including ${topicNames}. The Tier-2 exam consists of descriptive and objective questions based on CBSE/NCERT curriculum.`
        }
      },
      {
        "@type": "Question",
        "name": `How to prepare for KVS ${examType} ${subjectName} Tier-2 exam?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `For KVS ${examType} ${subjectName} Tier-2 preparation, study the complete syllabus on DescriptiveTest.com. Practice descriptive questions with AI-powered evaluation, take mock tests, solve MCQs, and study NCERT-based notes for all ${topicCount} topics.`
        }
      },
      {
        "@type": "Question",
        "name": `What is the exam pattern for EMRS ${examType} ${subjectName}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `EMRS ${examType} ${subjectName} Tier-2 exam consists of 15 descriptive questions (60 marks, 4 marks each) and 40 objective MCQs (40 marks). Total 100 marks in 3 hours. The syllabus covers ${topicCount} topics from CBSE/NCERT curriculum.`
        }
      }
    ]
  };

  // Scroll to top when page loads or subject/type changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type, subject]);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = localStorage.getItem('user');
    setIsLoggedIn(!!storedUser);
  }, []);

  const handleStartPractice = () => {
    if (isLoggedIn) {
      // If logged in, go directly to evaluate page
      navigate('/evaluate');
    } else {
      // Show auth modal
      setShowAuthModal(true);
    }
  };

  const handleAuthSuccess = (userData) => {
    // Save user data and redirect to evaluate page
    localStorage.setItem('user', JSON.stringify(userData));
    setShowAuthModal(false);
    // Use window.location to force full page reload so App.js picks up the new user state
    window.location.href = '/evaluate';
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="keywords" content={seoKeywords} />
        <meta property="og:title" content={`EMRS ${examType} ${subjectName} Syllabus 2026 | Descriptive Test`} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(syllabusSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      {/* Header */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <GraduationCap className="w-8 h-8 text-blue-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Descriptive Test
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content - 70% */}
          <div className="lg:w-[70%]">
            {/* TGT/PGT Toggle */}
            <div className="flex gap-2 mb-6">
              <Link to={`/syllabus/tgt/${subject}`}>
                <Button variant={!isPGT ? "default" : "outline"} className={!isPGT ? "bg-blue-600" : ""}>
                  TGT Syllabus
                </Button>
              </Link>
              <Link to={`/syllabus/pgt/${subject}`}>
                <Button variant={isPGT ? "default" : "outline"} className={isPGT ? "bg-purple-600" : ""}>
                  PGT Syllabus
                </Button>
              </Link>
            </div>

            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
                {data?.title || `EMRS ${isPGT ? 'PGT' : 'TGT'} ${subject} Syllabus`}
              </h1>
              {data?.titleHindi && (
                <p className="text-xl text-gray-600">{data.titleHindi}</p>
              )}
              <p className="text-gray-500 mt-2">
                Complete syllabus for EMRS {isPGT ? 'PGT' : 'TGT'} {subject?.replace('_', ' ')} - Tier 2 Examination
              </p>
            </div>

            <Card className="shadow-lg">
              <CardHeader className={`${isPGT ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'bg-gradient-to-r from-blue-600 to-purple-600'} text-white rounded-t-lg`}>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  {isPGT ? 'PGT' : 'TGT'} Syllabus Topics
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {data?.sections?.map((section, idx) => (
                  <SyllabusSection key={idx} section={section} />
                ))}
              </CardContent>
            </Card>

            {/* SEO Content */}
            <div className="mt-8 p-6 bg-white rounded-xl shadow-md">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                About EMRS {isPGT ? 'PGT' : 'TGT'} {subject?.replace('_', ' ')} Descriptive Paper
              </h2>
              <p className="text-gray-600 mb-4">
                The EMRS (Eklavya Model Residential Schools) Tier-2 examination for {isPGT ? 'PGT' : 'TGT'} {subject?.replace('_', ' ')} includes 
                {isPGT ? ' advanced level questions covering in-depth subject knowledge required for Post Graduate Teachers.' : ' questions covering fundamental concepts required for Trained Graduate Teachers.'}
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">EMRS {isPGT ? 'PGT' : 'TGT'} {subject}</span>
                <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">KVS NVS {isPGT ? 'PGT' : 'TGT'}</span>
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">Descriptive Paper 2025</span>
              </div>
            </div>

            {/* Descriptive Paper Practice Questions Section */}
            {descriptiveQuestions[subject] && (
              <Card className="mt-8 shadow-lg border-2 border-orange-200">
                <CardHeader className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-t-lg">
                  <CardTitle className="flex items-center gap-2 text-lg md:text-xl">
                    <BookOpen className="w-5 h-5" />
                    {isPGT ? 'PGT' : 'TGT'} {subject?.replace('_', ' ').toUpperCase()} DESCRIPTIVE QUESTIONS
                  </CardTitle>
                  <p className="text-orange-100 text-sm mt-1">
                    EMRS KVS NVS {isPGT ? 'PGT' : 'TGT'} {subject?.replace('_', ' ')} - 10 Important 4-Mark Questions
                  </p>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    {descriptiveQuestions[subject]?.map((q, idx) => (
                      <div 
                        key={q.id} 
                        className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {idx + 1}
                          </span>
                          <div className="flex-1">
                            <p className="text-gray-800 font-medium leading-relaxed">{q.question}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full">
                                {q.topic}
                              </span>
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                4 Marks
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-green-800 font-medium text-center">
                      📝 Practice these questions by uploading your handwritten answers and get instant expert feedback!
                    </p>
                    <Button 
                      onClick={handleStartPractice}
                      className="w-full mt-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    >
                      Start Practicing Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Advertisement Sidebar - 30% */}
          <div className="lg:w-[30%]">
            <div className="sticky top-24 space-y-6">
              {/* Practice CTA */}
              <Card className="bg-gradient-to-br from-orange-500 to-red-500 text-white shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">Practice Descriptive Questions</h3>
                  <p className="text-orange-100 mb-4">
                    Get instant CBSE Expert Team feedback on your handwritten answers for EMRS, KVS, NVS descriptive papers.
                  </p>
                  <ul className="space-y-2 mb-4 text-sm">
                    <li className="flex items-center gap-2">
                      <span className="bg-white/20 rounded-full p-1">✓</span>
                      CBSE pattern scoring (4 marks)
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="bg-white/20 rounded-full p-1">✓</span>
                      Instant feedback & scoring
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="bg-white/20 rounded-full p-1">✓</span>
                      Rank & Percentile comparison
                    </li>
                  </ul>
                  <Button 
                    onClick={handleStartPractice}
                    className="w-full bg-white text-orange-600 hover:bg-orange-100 font-bold"
                    data-testid="start-practice-btn"
                  >
                    Start Practice Now
                  </Button>
                </CardContent>
              </Card>

              {/* Pro Plan CTA - Hidden for now */}
              {/* 
              <Card className="bg-gradient-to-br from-purple-600 to-indigo-600 text-white shadow-xl">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">👑</span>
                    <h3 className="text-lg font-bold">Go Pro</h3>
                  </div>
                  <p className="text-purple-200 mb-4 text-sm">
                    Unlimited practice questions for serious EMRS/KVS/NVS aspirants.
                  </p>
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span>Basic</span>
                      <span className="font-bold">₹99 / 10 questions</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Standard</span>
                      <span className="font-bold">₹149 / 40 questions</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Unlimited</span>
                      <span className="font-bold">₹499 / month</span>
                    </div>
                  </div>
                  <Link to="/pro">
                    <Button className="w-full bg-yellow-400 text-purple-900 hover:bg-yellow-300 font-bold">
                      Upgrade to Pro
                    </Button>
                  </Link>
                </CardContent>
              </Card>
              */}

              {/* Other Subjects */}
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="text-base">Other {isNonTeaching ? 'Non-Teaching' : (isPGT ? 'PGT' : 'TGT')} Subjects</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {allSubjects.filter(s => s !== subject).slice(0, 5).map((s) => (
                    <Link key={s} to={`/syllabus/${type}/${s}`}>
                      <Button variant="outline" className="w-full justify-start text-sm mb-1" size="sm">
                        {s.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Button>
                    </Link>
                  ))}
                </CardContent>
              </Card>

              {/* Switch to TGT/PGT - only show for non Non-Teaching pages */}
              {!isNonTeaching && (
              <Card className="shadow-lg border-2 border-dashed border-gray-300">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-gray-600 mb-2">Looking for {isPGT ? 'TGT' : 'PGT'} syllabus?</p>
                  <Link to={`/syllabus/${isPGT ? 'tgt' : 'pgt'}/${subject}`}>
                    <Button variant="outline" className="w-full">
                      View {isPGT ? 'TGT' : 'PGT'} {subject?.replace(/_/g, ' ')} Syllabus
                    </Button>
                  </Link>
                </CardContent>
              </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400 mb-2">Best platform for EMRS, KVS, NVS descriptive paper preparation</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
            <Link to="/terms" className="hover:text-white">Terms & Policy</Link>
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/pro" className="hover:text-white">Pro Plans</Link>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        onSuccess={handleAuthSuccess}
      />
    </div>
  );
}

export { pgtSyllabusData, tgtSyllabusData, nonTeachingSyllabusData };
